import { useEffect, useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

// Helper function to compare dates without time component
const isDateBeforeToday = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Badge } from "../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { PageLayout } from "../components/page-layout"
import { TranslatedText } from "../components/translated-text"
import { useTranslation } from "../hooks/useTranslation"
import { getCompanyId } from "../lib/config"
import { Plus, Search, Filter, Eye, Edit, Send, Download, FileText, Users, Calculator, RefreshCw, CreditCard, CheckCircle, CheckCircle2, XCircle, MessageSquare, Bot, Calendar, ShoppingCart, X, Link, Loader2, Trash2, DollarSign, AlertTriangle, Package, Sparkles, ArrowRight } from "lucide-react"
import { MoonLoader } from "../components/ui/moon-loader"
import { useToast } from "../hooks/use-toast"
import apiService, { Estimate, RecurringInvoice } from "../lib/api"
import { useAuth } from "../contexts/auth-context"
import { getApiUrl } from "../lib/config"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table"
import { Label } from "../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { PaymentButtonCompact } from "../components/payment-button"
import { InvoiceEditDialog } from "../components/InvoiceEditDialog"
import { bankingApi, type BankAccount } from "../lib/api/banking"
import { InvoiceTemplate } from "../components/invoice-template"
import { InvoiceApproval } from "../components/invoice-approval"
import { Textarea } from "../components/ui/textarea"
import { NaturalLanguageInvoice } from "../components/natural-language-invoice"
import { AIAccountingChat } from "../components/ai-accounting-chat"
import { AccountingIntegrationStatus } from "../components/accounting-integration-status"
import { InvoicePDFGenerator } from "../lib/invoice-pdf-generator"
import { EnhancedRecurringInvoiceForm } from "../components/enhanced-recurring-invoice-form"

// Utility function to ensure SelectItem values are never empty
const safeSelectValue = (value: string | undefined | null): string => {
  if (!value || value.trim() === '') {
    return 'placeholder-value-' + Math.random().toString(36).substr(2, 9)
  }
  return value.trim()
}

// Helper function to format date for HTML date input
const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return ""
  try {
    // Handle different date formats
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ""
    return date.toISOString().slice(0, 10)
  } catch {
    return ""
  }
}

type InvoiceListItem = {
  id: string
  companyId: string
  customerId: string
  invoiceNumber: string
  issueDate: string
  dueDate?: string
  totalAmount: number
  balanceDue: number
  status: string
  currency?: string
  lines?: any[]
}

type CustomerListItem = {
  id: string
  name: string
  email?: string
  phone?: string
  taxNumber?: string
  currency?: string
  address?: string
}

export default function SalesPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  
  // Currency Conversion State
  const [convertedTotalAmount, setConvertedTotalAmount] = useState<number | null>(null)
  const [convertedOutstanding, setConvertedOutstanding] = useState<number | null>(null)
  const [convertedPaidThisMonth, setConvertedPaidThisMonth] = useState<number | null>(null)
  const [convertedOverdue, setConvertedOverdue] = useState<number | null>(null)
  const [convertingCurrency, setConvertingCurrency] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<string>(getCompanyId())

  // Listen for company changes from header
  useEffect(() => {
    const handleStorageChange = () => {
      const newCompanyId = getCompanyId();
      if (newCompanyId && newCompanyId !== selectedCompany) {
        setSelectedCompany(newCompanyId);
      }
    };

    // Listen for localStorage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (in case localStorage doesn't trigger)
    const handleCompanyChange = (e: CustomEvent) => {
      const newCompanyId = e.detail.companyId;
      if (newCompanyId && newCompanyId !== selectedCompany) {
        setSelectedCompany(newCompanyId);
      }
    };
    
    window.addEventListener('companyChanged', handleCompanyChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener);
    };
  }, [selectedCompany]);

  const [invoices, setInvoices] = useState<InvoiceListItem[]>([])
  const [customers, setCustomers] = useState<CustomerListItem[]>([])
  const [estimates, setEstimates] = useState<Estimate[]>([])
  const [recurringInvoices, setRecurringInvoices] = useState<RecurringInvoice[]>([])
  
  // Defensive wrapper for setCustomers to ensure it's always an array
  const setCustomersSafe = (newCustomers: any) => {
    const safeCustomers = Array.isArray(newCustomers) ? newCustomers : []
    setCustomers(safeCustomers)
  }
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false)

  // Helper function to generate PDF using frontend generator
  // Export sales data to CSV
  const exportSalesData = async () => {
    try {
      // Fetch all products first to create a lookup map
      const productsResponse = await apiService.getProducts({ companyId: selectedCompany }) as any
      const productsData = productsResponse?.items ?? productsResponse?.products ?? productsResponse?.data ?? productsResponse
      const allProducts = Array.isArray(productsData) ? productsData : []
      
      // Create a map of productId -> product for quick lookup
      const productsMap = new Map<string, any>()
      allProducts.forEach((product: any) => {
        if (product.id) {
          productsMap.set(product.id, product)
        }
      })

      // Fetch all invoices with their lines
      const allInvoicesData: any[] = []
      let page = 1
      const pageSize = 100
      let hasMore = true

      while (hasMore) {
        const response = await apiService.getInvoices({ 
          page, 
          pageSize, 
          companyId: selectedCompany 
        }) as any
        
        const invoiceData = response?.items ?? response?.invoices ?? response?.data ?? response
        const invoices = Array.isArray(invoiceData) ? invoiceData : []
        
        if (invoices.length === 0) {
          hasMore = false
        } else {
          // Use invoice data directly (lines should be included)
          allInvoicesData.push(...invoices)
          
          if (invoices.length < pageSize) {
            hasMore = false
          } else {
            page++
          }
        }
      }

      if (allInvoicesData.length === 0) {
        toast({
          title: 'No Data to Export',
          description: 'There are no invoices to export.',
          variant: 'default'
        })
        return
      }

      // Build export rows from invoice lines
      const exportRows: any[] = []
      
      for (const invoice of allInvoicesData) {
        const invoiceLines = invoice.lines || []
        const invoiceDate = invoice.issueDate ? new Date(invoice.issueDate).toISOString().split('T')[0] : ''
        const invoiceNumber = invoice.invoiceNumber || invoice.id || ''
        
        // Check if invoice uses global tax or per-line tax
        const taxMode = invoice.taxMode || 'per_line'
        const globalTaxRate = Number(invoice.globalTaxRate || 0)
        const invoiceTaxAmount = Number(invoice.taxAmount || 0)
        const invoiceDiscount = Number(invoice.discount || 0)
        const invoiceDiscountMode = invoice.discountMode || 'amount'
        
        // Calculate total line subtotal for distributing global tax
        let totalLineSubtotal = 0
        for (const line of invoiceLines) {
          const qty = Number(line.quantity || 0)
          const price = Number(line.unitPrice || 0)
          const lineDisc = Number(line.lineDiscount || 0)
          const lineSubtotal = qty * price
          const lineTotalAfterDiscount = Math.max(0, lineSubtotal - lineDisc)
          totalLineSubtotal += lineTotalAfterDiscount
        }
        
        // Apply invoice-level discount if applicable
        let totalAfterInvoiceDiscount = totalLineSubtotal
        if (invoiceDiscount > 0) {
          if (invoiceDiscountMode === 'percent') {
            totalAfterInvoiceDiscount = totalLineSubtotal * (1 - invoiceDiscount / 100)
          } else {
            totalAfterInvoiceDiscount = Math.max(0, totalLineSubtotal - invoiceDiscount)
          }
        }
        
        for (const line of invoiceLines) {
          const quantity = Number(line.quantity || 0)
          const unitPrice = Number(line.unitPrice || 0)
          const lineDiscount = Number(line.lineDiscount || 0)
          
          // Calculate line total after discount
          const lineSubtotal = quantity * unitPrice
          const lineTotalAfterDiscount = Math.max(0, lineSubtotal - lineDiscount)
          
          // Calculate tax for this line - matching recalcTotals logic from edit invoice
          // First, try to use the stored taxAmount from the line (most accurate)
          let taxAmount = Number(line.taxAmount || 0)
          
          // If line taxAmount is 0 or not available, calculate it using the same logic as recalcTotals
          if (taxAmount === 0 || isNaN(taxAmount)) {
            if (taxMode === 'global' && globalTaxRate > 0 && totalLineSubtotal > 0) {
              // For global tax: calculate on taxable base (after invoice discount), then distribute proportionally
              const taxableBase = Math.max(0, totalAfterInvoiceDiscount)
              const totalTax = taxableBase * (globalTaxRate / 100)
              const lineShare = lineTotalAfterDiscount / totalLineSubtotal
              taxAmount = totalTax * lineShare
            } else if (taxMode === 'per_line') {
              // For per-line tax: calculate on quantity * unitPrice (before line discount) - matching recalcTotals
              const taxRate = Number(line.taxRate || 0)
              if (taxRate > 0 && !isNaN(taxRate)) {
                // Match recalcTotals: tax is calculated on quantity * unitPrice, not on discounted amount
                taxAmount = quantity * unitPrice * (taxRate / 100)
              } else if (invoiceTaxAmount > 0 && totalLineSubtotal > 0) {
                // Fallback: if line doesn't have taxRate, distribute invoice tax proportionally based on line total
                const lineShare = lineTotalAfterDiscount / totalLineSubtotal
                taxAmount = invoiceTaxAmount * lineShare
              }
            } else if (invoiceTaxAmount > 0 && totalLineSubtotal > 0) {
              // Final fallback: if invoice has taxAmount, distribute it proportionally
              const lineShare = lineTotalAfterDiscount / totalLineSubtotal
              taxAmount = invoiceTaxAmount * lineShare
            }
          }
          
          // Total price includes tax
          const totalPrice = lineTotalAfterDiscount + taxAmount
          
          // Get product code/SKU if available
          // Check productCode first (direct field on line), then product relation, then lookup by productId, then by name match
          let itemCode = ''
          
          if (line.productCode) {
            itemCode = line.productCode
          } else if (line.product?.sku) {
            itemCode = line.product.sku
          } else if (line.product?.code) {
            itemCode = line.product.code
          } else if (line.productId) {
            // Look up product from products map by productId
            const product = productsMap.get(line.productId)
            
            if (product?.sku) {
              itemCode = product.sku
            } else if (product?.code) {
              itemCode = product.code
            } else {
              itemCode = line.productId
            }
          } else if (line.description) {
            // Try to find product by matching description/name
            const lineDescription = (line.description || '').trim().toLowerCase()
            const matchedProduct = Array.from(productsMap.values()).find((p: any) => {
              const productName = (p.name || '').trim().toLowerCase()
              return productName === lineDescription || productName.includes(lineDescription) || lineDescription.includes(productName)
            })
            
            if (matchedProduct?.sku) {
              itemCode = matchedProduct.sku
            } else if (matchedProduct?.code) {
              itemCode = matchedProduct.code
            }
          }
          
          const itemName = line.description || line.name || ''
          
          exportRows.push({
            date: invoiceDate,
            itemCode: itemCode,
            itemName: itemName,
            invoiceNumber: invoiceNumber,
            quantity: quantity.toFixed(2),
            unitPrice: unitPrice.toFixed(2),
            totalPrice: totalPrice.toFixed(2),
            tax: taxAmount.toFixed(2)
          })
        }
      }

      if (exportRows.length === 0) {
        toast({
          title: 'No Data to Export',
          description: 'There are no invoice lines to export.',
          variant: 'default'
        })
        return
      }

      // Create CSV content
      const headers = ['Date', 'Item N./code', 'Item Name', 'Invoice Number', 'Quantity', 'Unit price', 'Total price', 'Tax']
      
      // Helper function to escape CSV cells
      const escapeCSVCell = (value: any): string => {
        if (value === null || value === undefined || value === '') {
          return ''
        }
        let str = String(value).trim()
        if (str === '' || str === 'null' || str === 'undefined') {
          return ''
        }
        // Remove any existing surrounding quotes
        while (str.startsWith('"') && str.endsWith('"') && str.length > 1) {
          str = str.slice(1, -1)
        }
        // Replace double quotes with escaped quotes
        str = str.replace(/"/g, '""')
        // Quote if contains comma, quote, or newline
        if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
          return `"${str}"`
        }
        return str
      }

      const csvContent = [
        headers.map(escapeCSVCell).join(','),
        ...exportRows.map(row => [
          escapeCSVCell(row.date),
          escapeCSVCell(row.itemCode),
          escapeCSVCell(row.itemName),
          escapeCSVCell(row.invoiceNumber),
          escapeCSVCell(row.quantity),
          escapeCSVCell(row.unitPrice),
          escapeCSVCell(row.totalPrice),
          escapeCSVCell(row.tax)
        ].join(','))
      ].join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `sales-report-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      toast({
        title: 'Export Successful',
        description: `Exported ${exportRows.length} line items to CSV.`
      })
    } catch (error: any) {
      console.error('Export error:', error)
      toast({
        title: 'Export Failed',
        description: error?.message || 'Failed to export sales data. Please try again.',
        variant: 'destructive'
      })
    }
  }

  const handleDownloadInvoicePdf = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const invoice = invoices.find(inv => inv.id === invoiceId)
      if (!invoice) {
        toast({
          title: "Invoice Not Found",
          description: "Could not find invoice data for PDF generation.",
          variant: "destructive"
        })
        return
      }
      await generateInvoicePDF(invoice, 'download')
    } catch (error) {
      console.error('Error downloading PDF:', error)
      toast({
        title: "PDF Download Failed",
        description: "There was an error downloading the PDF. Please try again.",
        variant: "destructive"
      })
    }
  }

  const generateInvoicePDF = async (invoice: any, action: 'download' | 'preview' = 'download') => {
    try {
      const customer = customers.find(c => c.id === invoice.customerId)
      
      // Get company data with logo (same as journal PDF)
      let companyData = {
        name: 'Your Company',
        logoUrl: '',
        primaryColor: '#009688',
        website: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        email: '',
        phone: '',
        invoiceTerms: 'Payment is due within 30 days of invoice date.',
        invoiceFooter: 'Thank you for your business!'
      }
      
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
        if (companyId) {
          const company = await apiService.getCompany(companyId) as any
          if (company) {
            companyData.name = company.name || companyData.name
            let logoUrl = company.logoUrl || companyData.logoUrl
            
            // Convert relative URL to full URL for PDF generation
            if (logoUrl && logoUrl.startsWith('/api/')) {
              logoUrl = getApiUrl(logoUrl)
            }
            
            companyData.logoUrl = logoUrl
            companyData.primaryColor = company.primaryColor || companyData.primaryColor
            companyData.website = company.website || companyData.website
            companyData.address = company.address || companyData.address
            companyData.city = company.city || companyData.city
            companyData.state = company.state || companyData.state
            companyData.postalCode = company.postalCode || companyData.postalCode
            companyData.email = company.email || companyData.email
            companyData.phone = company.phone || companyData.phone
          }
        }
      } catch (error) {
        // Use fallback data if API fails
        companyData = {
          name: localStorage.getItem('company_name') || 'Your Company',
          logoUrl: localStorage.getItem('company_logoUrl') || '',
          primaryColor: localStorage.getItem('company_primaryColor') || '#009688',
          website: localStorage.getItem('company_website') || '',
          address: localStorage.getItem('company_address') || '',
          city: localStorage.getItem('company_city') || '',
          state: localStorage.getItem('company_state') || '',
          postalCode: localStorage.getItem('company_postalCode') || '',
          email: localStorage.getItem('company_email') || '',
          phone: localStorage.getItem('company_phone') || '',
          invoiceTerms: 'Payment is due within 30 days of invoice date.',
          invoiceFooter: 'Thank you for your business!'
        }
      }
      
      const generator = new InvoicePDFGenerator({
        invoice: {
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate,
          status: invoice.status,
          totalAmount: invoice.totalAmount,
          balanceDue: invoice.balanceDue,
          currency: customer?.currency || 'Frw',
          subtotal: invoice.subtotal || invoice.totalAmount,
          taxAmount: invoice.taxAmount || 0,
          discountAmount: invoice.discountAmount || 0,
          customer: customer ? {
            name: customer.name,
            email: customer.email,
            address: customer.address,
            phone: (customer as any).phone,
            taxId: (customer as any).taxId
          } : undefined,
          lines: (invoice.lines || []).map((line: any) => ({
            description: line.description || '',
            quantity: Number(line.quantity) || 0,
            unitPrice: Number(line.unitPrice) || 0,
            lineTotal: Number(line.lineTotal) || (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0),
            taxRate: Number(line.taxRate) || 0,
            discountRate: Number(line.discountRate) || 0,
            discountAmount: Number(line.discountAmount) || 0,
            taxAmount: Number(line.taxAmount) || 0
          })),
          notes: invoice.notes,
          paymentUrl: invoice.paymentUrl
        },
        company: companyData
      })

      if (action === 'download') {
        await generator.download()
        toast({
          title: "PDF Downloaded",
          description: `Invoice ${invoice.invoiceNumber} has been downloaded successfully.`
        })
      } else {
        const blob = await generator.generate()
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 30000)
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast({
        title: "PDF Generation Failed",
        description: "There was an error generating the PDF. Please try again.",
        variant: "destructive"
      })
      
      // Fallback to backend API
      try {
        const blob = await apiService.getInvoicePdf(invoice.id)
        const url = URL.createObjectURL(blob)
        if (action === 'download') {
          const a = document.createElement('a')
          a.href = url
          a.download = `invoice-${invoice.invoiceNumber}.pdf`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        } else {
          window.open(url, '_blank')
        }
        setTimeout(() => URL.revokeObjectURL(url), 30000)
      } catch (backendError) {
        console.error('Backend PDF generation also failed:', backendError)
      }
    }
  }
  const [editingCustomer, setEditingCustomer] = useState<CustomerListItem | null>(null)
  const [customerForm, setCustomerForm] = useState<{ name: string; email?: string; phone?: string; tin?: string; currency?: string }>({ name: "", email: "", phone: "", tin: "", currency: 'Frw' })
  const [customerSaving, setCustomerSaving] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)

  const [invoiceSearch, setInvoiceSearch] = useState("")
  const [customerSearch, setCustomerSearch] = useState("")
  const [invoicePage, setInvoicePage] = useState(1)
  const [invoicePageSize, setInvoicePageSize] = useState(10)
  const [customerPage, setCustomerPage] = useState(1)
  const [customerPageSize, setCustomerPageSize] = useState(1000)

  const [estimateSearch, setEstimateSearch] = useState("")
  const [estimatePage, setEstimatePage] = useState(1)
  const [estimatePageSize, setEstimatePageSize] = useState(10)
  const [recurringPage, setRecurringPage] = useState(1)
  const [recurringPageSize, setRecurringPageSize] = useState(10)
  const [recurringSearch, setRecurringSearch] = useState("")
  const [recurringStatus, setRecurringStatus] = useState<string>("")
  const [selectedRecurring, setSelectedRecurring] = useState<RecurringInvoice | null>(null)
  const [recurringDetailsOpen, setRecurringDetailsOpen] = useState(false)
  const [recurringHistory, setRecurringHistory] = useState<any[]>([])
  const [recurringHistoryLoading, setRecurringHistoryLoading] = useState(false)

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [invoiceSaving, setInvoiceSaving] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  type InvoiceLine = { description: string; quantity: number; unitPrice: number; taxRate: number; lineDiscount?: number; productId?: string }
  const [invoiceForm, setInvoiceForm] = useState<{ customerId: string; invoiceNumber: string; currency: string; issueDate: string; dueDate?: string; discountMode: 'amount'|'percent'; discount: number; shipping: number; taxMode: 'per_line'|'global'; globalTaxRate: number; lines: InvoiceLine[]; subtotal: number; taxTotal: number; totalAmount: number; notes?: string; terms?: string; paymentTerms?: string }>(
    { customerId: "", invoiceNumber: "", currency: 'Frw', issueDate: new Date().toISOString().slice(0,10), dueDate: "", discountMode: 'amount', discount: 0, shipping: 0, taxMode: 'per_line', globalTaxRate: 0, lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "" }], subtotal: 0, taxTotal: 0, totalAmount: 0, notes: "", terms: "Payment due within 30 days", paymentTerms: "Net 30" }
  )

  // Estimate modal state
  type EstimateLineForm = { description: string; quantity: number; unitPrice: number; taxRate: number }
  const [estimateDialogOpen, setEstimateDialogOpen] = useState(false)
  const [estimateSaving, setEstimateSaving] = useState(false)
  const [estimateError, setEstimateError] = useState<string | null>(null)
  const [estimateForm, setEstimateForm] = useState<{ customerId: string; estimateNumber: string; issueDate: string; expiryDate?: string; currency: string; notes?: string; terms?: string; lines: EstimateLineForm[] }>(
    { customerId: "", estimateNumber: "", issueDate: new Date().toISOString().slice(0,10), expiryDate: "", currency: 'Frw', notes: "", terms: "", lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }] }
  )

  // Estimate view/edit state
  const [estimateViewDialogOpen, setEstimateViewDialogOpen] = useState(false)
  const [estimateEditDialogOpen, setEstimateEditDialogOpen] = useState(false)
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(null)
  const [estimateViewLoading, setEstimateViewLoading] = useState(false)
  const [estimateEditForm, setEstimateEditForm] = useState<{ customerId: string; estimateNumber: string; issueDate: string; expiryDate?: string; currency: string; notes?: string; terms?: string; lines: EstimateLineForm[] }>(
    { customerId: "", estimateNumber: "", issueDate: new Date().toISOString().slice(0,10), expiryDate: "", currency: 'Frw', notes: "", terms: "", lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }] }
  )

  const recalcTotals = (lines: InvoiceLine[], discount = invoiceForm.discount, shipping = invoiceForm.shipping, taxMode = invoiceForm.taxMode, globalTaxRate = invoiceForm.globalTaxRate, discountMode = invoiceForm.discountMode) => {
    const lineSubtotal = lines.reduce((sum, l) => {
      const gross = l.quantity * l.unitPrice
      const ld = Math.max(0, Math.min(gross, l.lineDiscount || 0))
      return sum + (gross - ld)
    }, 0)
    const discountAmt = discountMode === 'percent' ? Math.max(0, Math.min(100, discount)) * lineSubtotal / 100 : Math.max(0, Math.min(discount, lineSubtotal))
    const taxableBase = Math.max(0, lineSubtotal - discountAmt)
    const lineTax = taxMode === 'per_line'
      ? lines.reduce((sum, l) => sum + (l.quantity * l.unitPrice * (l.taxRate || 0) / 100), 0)
      : taxableBase * (globalTaxRate || 0) / 100
    const subtotal = taxableBase
    const taxTotal = lineTax
    const totalAmount = subtotal + taxTotal + (shipping || 0)
    return { subtotal, taxTotal, totalAmount }
  }

  const recalcRecurringTotals = (lines: RecurringLine[]) => {
    const subtotal = lines.reduce((sum, l) => sum + (l.quantity * l.unitPrice), 0)
    const taxTotal = lines.reduce((sum, l) => sum + (l.quantity * l.unitPrice * (l.taxRate || 0) / 100), 0)
    const totalAmount = subtotal + taxTotal
    return { subtotal, taxTotal, totalAmount }
  }

  const [products, setProducts] = useState<any[]>([])
  const [autoConvertPrices, setAutoConvertPrices] = useState(true)
  const [lastPriceCurrency, setLastPriceCurrency] = useState<string>('Frw')
  const [exchangeRate, setExchangeRate] = useState<number>(1)
  const [lockRate, setLockRate] = useState<boolean>(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsInvoice, setDetailsInvoice] = useState<InvoiceListItem | null>(null)
  const [detailsPdfUrl, setDetailsPdfUrl] = useState<string | null>(null)
  const [detailsReconciliationStatus, setDetailsReconciliationStatus] = useState<{
    isReconciled: boolean
    reconciledPayments: number
    totalPayments: number
    bankTransactions: Array<{ id: string; amount: number; date: string; description: string }>
  } | null>(null)
  
  // Recurring invoice dialog state
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false)
  const [editingRecurring, setEditingRecurring] = useState<RecurringInvoice | null>(null)
  const [recurringSaving, setRecurringSaving] = useState(false)
  const [recurringError, setRecurringError] = useState<string | null>(null)
  type RecurringLine = { description: string; quantity: number; unitPrice: number; taxRate: number }
  const [recurringForm, setRecurringForm] = useState<{ 
    customerId: string; 
    name: string; 
    description?: string; 
    frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom'; 
    interval: number; 
    startDate: string; 
    endDate?: string; 
    currency: string; 
    notes?: string; 
    terms?: string; 
    dueDateOffset: number; 
    autoSend: boolean; 
    emailTemplate?: string;
    
    // Advanced Scheduling
    dayOfWeek?: number;
    dayOfMonth?: number;
    businessDaysOnly: boolean;
    skipHolidays: boolean;
    timezone: string;
    
    // Conditional Logic
    skipIfOutstandingBalance: boolean;
    maxOutstandingAmount?: number;
    skipIfCustomerInactive: boolean;
    requireApproval: boolean;
    approvalWorkflowId?: string;
    
    // Email Settings
    ccEmails: string[];
    bccEmails: string[];
    reminderDays: number[];
    
    lines: RecurringLine[]; 
    subtotal: number; 
    taxTotal: number; 
    totalAmount: number 
  }>({
    customerId: "", 
    name: "", 
    description: "", 
    frequency: 'monthly', 
    interval: 1, 
    startDate: new Date().toISOString().slice(0,10), 
    endDate: "", 
    currency: 'Frw', 
    notes: "", 
    terms: "", 
    dueDateOffset: 30, 
    autoSend: false, 
    emailTemplate: "",
    
    // Advanced Scheduling
    dayOfWeek: undefined,
    dayOfMonth: undefined,
    businessDaysOnly: false,
    skipHolidays: false,
    timezone: "UTC",
    
    // Conditional Logic
    skipIfOutstandingBalance: false,
    maxOutstandingAmount: undefined,
    skipIfCustomerInactive: false,
    requireApproval: false,
    approvalWorkflowId: undefined,
    
    // Email Settings
    ccEmails: [],
    bccEmails: [],
    reminderDays: [],
    
    lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }], 
    subtotal: 0, 
    taxTotal: 0, 
    totalAmount: 0 
  })
  const [detailsPayLink, setDetailsPayLink] = useState<string | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsStatus, setDetailsStatus] = useState<string>('draft')
  const [detailsDueDate, setDetailsDueDate] = useState<string>('')
  const [detailsSaving, setDetailsSaving] = useState(false)
  const [detailsActivity, setDetailsActivity] = useState<Array<{ id: string; type: string; at: string; by?: string; message?: string }>>([])
  // Send Invoice PDF modal state
  const [sendEmailOpen, setSendEmailOpen] = useState(false)
  const [sendEmailInvoiceId, setSendEmailInvoiceId] = useState<string | null>(null)
  const [sendEmailTo, setSendEmailTo] = useState<string>("")
  const [sendEmailLoading, setSendEmailLoading] = useState(false)

  // Credit note dialog state
  const [creditDialogOpen, setCreditDialogOpen] = useState(false)
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [invoiceToDelete, setInvoiceToDelete] = useState<{id: string, invoiceNumber: string} | null>(null)
  
  // Deleted invoices state
  const [showDeletedInvoices, setShowDeletedInvoices] = useState(false)
  
  // Loading states for invoice actions
  const [markingAsSent, setMarkingAsSent] = useState<string | null>(null)
  const [markingAsPaid, setMarkingAsPaid] = useState<string | null>(null)
  const [deletingInvoice, setDeletingInvoice] = useState<string | null>(null)
  
  // Invoice editing state
  const [editingInvoice, setEditingInvoice] = useState<any>(null)
  const [creditReason, setCreditReason] = useState('Customer return')
  const [creditInvoiceId, setCreditInvoiceId] = useState<string | null>(null)

  // Default reset shape for recurring form (ensures all required fields are present)
  const defaultRecurringForm = {
    customerId: "",
    name: "",
    description: "",
    frequency: 'monthly' as const,
    interval: 1,
    startDate: new Date().toISOString().slice(0,10),
    endDate: "",
    currency: 'Frw',
    notes: "",
    terms: "",
    dueDateOffset: 30,
    autoSend: false,
    emailTemplate: "",
    // Advanced Scheduling
    dayOfWeek: undefined,
    dayOfMonth: undefined,
    businessDaysOnly: false,
    skipHolidays: false,
    timezone: "UTC",
    // Conditional Logic
    skipIfOutstandingBalance: false,
    maxOutstandingAmount: undefined,
    skipIfCustomerInactive: false,
    requireApproval: false,
    approvalWorkflowId: undefined,
    // Email Settings
    ccEmails: [] as string[],
    bccEmails: [] as string[],
    reminderDays: [] as number[],
    // Lines and totals
    lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }],
    subtotal: 0,
    taxTotal: 0,
    totalAmount: 0
  }

  const prevLinesConvert = (lines: InvoiceLine[], rate: number) => {
    return lines.map(l => ({ ...l, unitPrice: (l.unitPrice || 0) * (rate || 1), lineDiscount: (l.lineDiscount || 0) * (rate || 1) }))
  }

  const formatCurrency = (amount: any, currency: string) => {
    const amt = Number(amount) || 0
    // Format as "Amount Frw" (e.g., "1,000.00 Frw")
    if (currency === 'Frw' || currency === 'FRW' || currency === 'RWF') {
      return `${amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw`
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amt)
    } catch {
      try {
        return `$${amt.toFixed(2)}`
      } catch {
        return `$${amt}`
      }
    }
  }

  // Convert USD amounts to Frw for display
  const [convertedInvoiceAmounts, setConvertedInvoiceAmounts] = useState<Record<string, { totalAmount: number; balanceDue: number }>>({})
  const [convertingInvoiceCurrency, setConvertingInvoiceCurrency] = useState<Record<string, boolean>>({})

  // Convert invoice amounts from USD to Frw when currency is USD
  useEffect(() => {
    const convertInvoiceAmounts = async () => {
      if (!invoices || invoices.length === 0) return
      
      const invoicesToConvert = invoices.filter(inv => {
        const customer = (Array.isArray(customers) ? customers : []).find(c => c.id === inv.customerId)
        const currency = customer?.currency || 'Frw'
        return currency === 'USD' && (inv.totalAmount > 0 || inv.balanceDue > 0)
      })

      for (const inv of invoicesToConvert) {
        if (convertingInvoiceCurrency[inv.id]) continue
        
        setConvertingInvoiceCurrency(prev => ({ ...prev, [inv.id]: true }))
        try {
          const conversions = await Promise.all([
            inv.totalAmount > 0 ? bankingApi.convertCurrency(inv.totalAmount, 'USD', 'FRW').catch(() => null) : Promise.resolve(null),
            inv.balanceDue > 0 ? bankingApi.convertCurrency(inv.balanceDue, 'USD', 'FRW').catch(() => null) : Promise.resolve(null)
          ])
          
          const convertedTotal = conversions[0]?.conversion?.convertedAmount || conversions[0]?.convertedAmount || inv.totalAmount
          const convertedBalance = conversions[1]?.conversion?.convertedAmount || conversions[1]?.convertedAmount || inv.balanceDue
          
          setConvertedInvoiceAmounts(prev => ({
            ...prev,
            [inv.id]: { totalAmount: convertedTotal, balanceDue: convertedBalance }
          }))
        } catch (error) {
          console.error('Invoice conversion error:', error)
          setConvertedInvoiceAmounts(prev => ({
            ...prev,
            [inv.id]: { totalAmount: inv.totalAmount, balanceDue: inv.balanceDue }
          }))
        } finally {
          setConvertingInvoiceCurrency(prev => ({ ...prev, [inv.id]: false }))
        }
      }
    }

    convertInvoiceAmounts()
  }, [invoices, customers])

  const qc = useQueryClient()

  // Best-practice validity check for recurring invoice form
  const isRecurringFormValid = (f: typeof recurringForm) => {
    const hasBasics = Boolean(f.name && f.name.trim() && f.customerId && f.startDate && f.frequency)
    const hasLines = Array.isArray(f.lines) && f.lines.length > 0
    const linesOk = hasLines && f.lines.every(l => {
      const q = Number(l.quantity)
      const p = Number(l.unitPrice)
      const t = Number(l.taxRate)
      return !Number.isNaN(q) && q >= 0 && !Number.isNaN(p) && p >= 0 && !Number.isNaN(t) && t >= 0 && t <= 100
    })
    return hasBasics && hasLines && linesOk
  }

  // Fetch companies for the company selector
  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiService.getCompanies(),
    enabled: isAuthenticated && !authLoading,
    staleTime: 5 * 60 * 1000
  })

  // Helper function to get company data
  const getCompanyData = () => {
    const companies = companiesQuery.data as any
    if (Array.isArray(companies)) {
      return companies.find((c: any) => c.id === selectedCompany)
    }
    return null
  }


  // Initialize company selection from localStorage or default
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedCompanyId = localStorage.getItem('company_id')
      if (storedCompanyId) {
        setSelectedCompany(storedCompanyId)
      }
    }
  }, [])

  // Update localStorage when company changes
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedCompany) {
      localStorage.setItem('company_id', selectedCompany)
    }
  }, [selectedCompany])

  // Populate invoice form when editing
  useEffect(() => {
    if (editingInvoice && invoiceDialogOpen) {
      // Convert the invoice data to the form format
      const formData = {
        customerId: editingInvoice.customerId || "",
        invoiceNumber: editingInvoice.invoiceNumber || "",
        currency: editingInvoice.currency || "Frw",
        issueDate: editingInvoice.issueDate ? new Date(editingInvoice.issueDate).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
        dueDate: editingInvoice.dueDate ? new Date(editingInvoice.dueDate).toISOString().slice(0,10) : "",
        discountMode: 'amount' as 'amount'|'percent',
        discount: editingInvoice.discountAmount || 0,
        shipping: editingInvoice.shippingAmount || 0,
        taxMode: 'per_line' as 'per_line'|'global',
        globalTaxRate: 0,
        lines: editingInvoice.lines?.map((line: any) => ({
          description: line.description || "",
          quantity: line.quantity || 1,
          unitPrice: line.unitPrice || 0,
          taxRate: line.taxRate || 0,
          productId: line.productId || ""
        })) || [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "" }],
        notes: editingInvoice.notes || "",
        terms: editingInvoice.terms || "Payment due within 30 days",
        paymentTerms: editingInvoice.paymentTerms || "Net 30",
        subtotal: editingInvoice.subtotal || 0,
        taxTotal: editingInvoice.taxAmount || 0,
        totalAmount: editingInvoice.totalAmount || 0
      };
      
      setInvoiceForm(formData);
    } else if (!editingInvoice && invoiceDialogOpen) {
      // Reset form for new invoice
      const startLines = [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "" }];
      const safeCustomers = Array.isArray(customers) ? customers : [];
      const initCurrency = safeCustomers[0]?.currency || 'Frw';
      setLastPriceCurrency(initCurrency);
      setInvoiceForm({ 
        customerId: safeCustomers[0]?.id || "", 
        invoiceNumber: "", 
        currency: initCurrency, 
        issueDate: new Date().toISOString().slice(0,10), 
        dueDate: "", 
        discountMode: 'amount', 
        discount: 0, 
        shipping: 0, 
        taxMode: 'per_line', 
        globalTaxRate: 0, 
        lines: startLines, 
        notes: "", 
        terms: "Payment due within 30 days", 
        paymentTerms: "Net 30", 
        ...recalcTotals(startLines, 0, 0, 'per_line', 0, 'amount') 
      });
    }
  }, [editingInvoice, invoiceDialogOpen, customers])

  const createRecurringInvoiceMutation = useMutation({
    mutationFn: (data: any) => {
      return apiService.createRecurringInvoice({ ...data, companyId: selectedCompany })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-invoices'] })
      setRecurringDialogOpen(false)
      setRecurringForm({ ...defaultRecurringForm, startDate: new Date().toISOString().slice(0,10) })
      setEditingRecurring(null)
    }
  })

  const updateRecurringInvoiceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      return apiService.updateRecurringInvoice(id, data)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-invoices'] })
      setRecurringDialogOpen(false)
      setRecurringForm({ ...defaultRecurringForm, startDate: new Date().toISOString().slice(0,10) })
      setEditingRecurring(null)
    }
  })

  // Update recurring invoice status
  const updateRecurringStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' | 'completed' | 'cancelled' }) => {
      return apiService.updateRecurringInvoiceStatus(id, status)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-invoices'] })
      toast({ title: "Success", description: "Recurring invoice status updated" })
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to update status", variant: "destructive" })
    }
  })

  // Generate invoice from recurring template
  const generateRecurringMutation = useMutation({
    mutationFn: (id: string) => {
      return apiService.generateInvoiceFromRecurring(id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-invoices'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      toast({ title: "Success", description: "Invoice generated successfully" })
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to generate invoice", variant: "destructive" })
    }
  })

  // Load recurring invoice history
  const loadRecurringHistory = async (id: string) => {
    setRecurringHistoryLoading(true)
    try {
      const response = await apiService.getRecurringInvoiceHistory(id)
      setRecurringHistory(response.invoices)
    } catch (error) {
      toast({ title: "Error", description: "Failed to load invoice history", variant: "destructive" })
    } finally {
      setRecurringHistoryLoading(false)
    }
  }

  // Handler functions
  const handleEditRecurring = (recurring: RecurringInvoice) => {
    setEditingRecurring(recurring)
    setRecurringForm({
      customerId: recurring.customerId,
      name: recurring.name,
      description: recurring.description || '',
      frequency: recurring.frequency as any,
      interval: recurring.interval,
      startDate: (recurring.startDate || '').slice(0,10),
      endDate: recurring.endDate ? recurring.endDate.slice(0,10) : '',
      currency: recurring.currency,
      notes: recurring.notes || '',
      terms: recurring.terms || '',
      dueDateOffset: recurring.dueDateOffset,
      autoSend: recurring.autoSend,
      emailTemplate: recurring.emailTemplate || '',
      // Advanced Scheduling defaults (not stored yet)
      dayOfWeek: undefined,
      dayOfMonth: undefined,
      businessDaysOnly: false,
      skipHolidays: false,
      timezone: 'UTC',
      // Conditional Logic defaults
      skipIfOutstandingBalance: false,
      maxOutstandingAmount: undefined,
      skipIfCustomerInactive: false,
      requireApproval: false,
      approvalWorkflowId: undefined,
      // Email Settings defaults
      ccEmails: [],
      bccEmails: [],
      reminderDays: [],
      lines: recurring.lines?.map(line => ({
        description: line.description || '',
        quantity: Number(line.quantity),
        unitPrice: Number(line.unitPrice),
        taxRate: Number(line.taxRate)
      })) || [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }],
      subtotal: Number(recurring.totalAmount),
      taxTotal: 0,
      totalAmount: Number(recurring.totalAmount)
    })
    setRecurringDialogOpen(true)
  }

  const handleViewRecurring = (recurring: RecurringInvoice) => {
    setSelectedRecurring(recurring)
    setRecurringDetailsOpen(true)
    loadRecurringHistory(recurring.id)
  }

  const handleGenerateRecurring = (recurring: RecurringInvoice) => {
    generateRecurringMutation.mutate(recurring.id)
  }

  const handleStatusChange = (recurring: RecurringInvoice, newStatus: 'active' | 'paused' | 'completed' | 'cancelled') => {
    updateRecurringStatusMutation.mutate({ id: recurring.id, status: newStatus })
  }

  const handleDeleteInvoice = async (invoiceId: string) => {
    setDeletingInvoice(invoiceId);
    try {
      await apiService.deleteInvoice(invoiceId);
      toast({
        title: 'Success',
        description: 'Invoice deleted successfully',
        variant: 'default'
      });
      // Invalidate and refetch invoices
      qc.invalidateQueries({ queryKey: ["invoices"] });
      // Close the confirmation dialog
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        variant: 'destructive'
      });
    } finally {
      setDeletingInvoice(null);
    }
  }

  const handleRestoreInvoice = async (invoiceId: string) => {
    try {
      await apiService.restoreInvoice(invoiceId);
      toast({
        title: 'Success',
        description: 'Invoice restored successfully',
        variant: 'default'
      });
      // Invalidate and refetch invoices
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["deleted-invoices"] });
    } catch (error) {
      console.error('Error restoring invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore invoice',
        variant: 'destructive'
      });
    }
  }

  // Handle marking invoice as paid with bank account from invoice data
  const handleMarkAsPaid = async (invoice: any) => {
    setMarkingAsPaid(invoice.id)
    try {
      // First, try to process accounting entries and inventory updates
      const accountingResult = await apiService.processInvoicePayment(invoice.id)
      
      // Update invoice status after successful accounting processing
      await apiService.updateInvoice(invoice.id, { status: 'paid', balanceDue: 0 })
      
      // Update bank balance when invoice is marked as paid
      // Reconciliation will only mark transaction as reconciled (won't update balance again)
      if (invoice.bankAccountId) {
        try {
          // Create bank transaction and update balance immediately
          await bankingApi.createBankTransaction({
            bankAccountId: invoice.bankAccountId,
            transactionDate: new Date().toISOString(),
            amount: invoice.totalAmount,
            transactionType: 'credit',
            description: `Invoice Payment - ${invoice.invoiceNumber}`,
            reference: `PAY-${invoice.invoiceNumber}`,
            status: 'unreconciled',
            isReconciled: false,
            category: 'Revenue & Sales',
            updateBalance: true // Update bank balance when invoice is marked as paid
          } as any)
        } catch (bankError) {
          console.error('Failed to create bank transaction:', bankError)
          // Don't fail the entire transaction, just log the error
        }
      }
      
      // Show success message
      toast({
        title: 'Invoice marked as paid',
        description: `Accounting entries created, inventory updated${invoice.bankAccountId ? ' and bank transaction created (balance will update when bank statement matches)' : ''}. Journal Entry: ${accountingResult.journalEntryId}`,
        variant: 'default'
      })
      
      // Invalidate queries to refresh data
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["invoices"] }),
        qc.invalidateQueries({ queryKey: ["journal-entries"] }),
        qc.invalidateQueries({ queryKey: ["inventory-movements"] }),
        qc.invalidateQueries({ queryKey: ["bank-accounts"] })
      ])
      
    } catch (accountingError) {
      // Check if it's an inventory validation error
      if ((accountingError as any).message && (accountingError as any).message.includes('Insufficient inventory')) {
        toast({
          title: 'Insufficient Inventory',
          description: (accountingError as any).message,
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Error',
          description: 'Failed to process invoice payment. Please check inventory and try again.',
          variant: 'destructive'
        })
      }
    } finally {
      setMarkingAsPaid(null)
    }
  }


  const invoicesQuery = useQuery({
    queryKey: ["invoices", invoicePage, invoicePageSize, selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    queryFn: async () => {
      const invResp = await apiService.getInvoices({ page: invoicePage, pageSize: invoicePageSize, companyId: selectedCompany })
      const raw = invResp as any
      const invData = raw?.items ?? raw?.invoices ?? raw?.data ?? raw
      return Array.isArray(invData) ? invData as InvoiceListItem[] : []
    }
  })
  const customersQuery = useQuery({
    queryKey: ["customers", customerPage, customerPageSize, customerSearch, selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    queryFn: async () => {
      const custResp = await apiService.getCustomers({ 
        page: customerPage, 
        pageSize: customerPageSize, 
        companyId: selectedCompany,
        q: customerSearch || undefined
      })
      return custResp
    }
  })

  // Fetch all customers for selectors (unpaginated up to 1000)
  const customersAllQuery = useQuery({
    queryKey: ["customers-all", selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Backend caps pageSize at 100; fetch all pages and merge
      const pageSize = 100
      const first = await apiService.getCustomers({ page: 1, pageSize, companyId: selectedCompany }) as any
      const total = first?.total ?? (first?.items?.length || 0)
      let items = Array.isArray(first?.items) ? first.items : []
      const totalPages = first?.totalPages || Math.ceil(total / pageSize) || 1
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => apiService.getCustomers({ page: i + 2, pageSize, companyId: selectedCompany }) as any)
        )
        for (const r of rest) {
          if (Array.isArray(r?.items)) items = items.concat(r.items)
        }
      }
      return { items, total }
    }
  })

  const allCustomers = useMemo(() => {
    
    const list: any = (customersAllQuery.data?.items ?? customersAllQuery.data ?? customers)
    return Array.isArray(list) ? list : []
  }, [customersAllQuery.data, customers])

  const estimatesQuery = useQuery({
    queryKey: ["estimates", estimatePage, estimatePageSize, estimateSearch, selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    queryFn: async () => {
      const estResp = await apiService.getEstimates({ 
        page: estimatePage,
        pageSize: estimatePageSize,
        companyId: selectedCompany,
        q: estimateSearch || undefined
      })
      const raw = estResp as any
      const estData = raw?.items ?? raw?.estimates ?? raw?.data ?? raw
      return Array.isArray(estData) ? estData : []
    }
  })

  const recurringInvoicesQuery = useQuery({
    queryKey: ["recurring-invoices", recurringPage, recurringPageSize, recurringSearch, recurringStatus, selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    queryFn: async () => {
      const recResp = await apiService.getRecurringInvoices({ 
        page: recurringPage, 
        pageSize: recurringPageSize, 
        companyId: selectedCompany,
        q: recurringSearch || undefined,
        status: recurringStatus || undefined
      })
      return recResp
    }
  })
  
  const productsQuery = useQuery({
    queryKey: ["products", selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    queryFn: async () => {
      const prodResp = await apiService.getProducts({ companyId: selectedCompany }) as any
      const prodData = prodResp?.items ?? prodResp?.products ?? prodResp?.data ?? prodResp
      return Array.isArray(prodData) ? prodData : []
    }
  })

  const deletedInvoicesQuery = useQuery({
    queryKey: ["deleted-invoices", selectedCompany],
    enabled: isAuthenticated && !authLoading && !!selectedCompany,
    queryFn: async () => {
      const deletedResp = await apiService.getDeletedInvoices({ 
        companyId: selectedCompany,
        page: 1,
        pageSize: 100
      })
      return deletedResp.items || []
    }
  })

  useEffect(() => {
    
    if (invoicesQuery.data) setInvoices(invoicesQuery.data)
    if (customersQuery.data) {
      const customerItems = customersQuery.data?.items || customersQuery.data
      setCustomersSafe(Array.isArray(customerItems) ? customerItems : [])
    }
    if (estimatesQuery.data) setEstimates(estimatesQuery.data)
    if (recurringInvoicesQuery.data) {
      const resp: any = recurringInvoicesQuery.data as any
      const items = resp?.items ?? []
      setRecurringInvoices(items as any[])
    }
    if (productsQuery.data) setProducts(productsQuery.data as any)
    setLoading(invoicesQuery.isLoading || customersQuery.isLoading || estimatesQuery.isLoading || recurringInvoicesQuery.isLoading || productsQuery.isLoading)
    setError((invoicesQuery.error as any)?.message || (customersQuery.error as any)?.message || (estimatesQuery.error as any)?.message || (recurringInvoicesQuery.error as any)?.message || (productsQuery.error as any)?.message || null)
  }, [invoicesQuery.data, customersQuery.data, estimatesQuery.data, recurringInvoicesQuery.data, productsQuery.data, invoicesQuery.isLoading, customersQuery.isLoading, estimatesQuery.isLoading, recurringInvoicesQuery.isLoading, productsQuery.isLoading, invoicesQuery.error, customersQuery.error, estimatesQuery.error, recurringInvoicesQuery.error, productsQuery.error])

  useEffect(() => { setMounted(true) }, [])

  const filteredInvoices = useMemo(() => {
    if (!invoiceSearch) return invoices
    const q = invoiceSearch.toLowerCase()
    return invoices.filter(inv =>
      inv.invoiceNumber.toLowerCase().includes(q) ||
      inv.status.toLowerCase().includes(q)
    )
  }, [invoices, invoiceSearch])

  const filteredCustomers = useMemo(() => {
    const safeCustomers = Array.isArray(customers) ? customers : []
    return safeCustomers
  }, [customers])

  // Estimates to display (data is already processed in queryFn)
  const displayEstimates = useMemo(() => {
    return Array.isArray(estimates) ? estimates : []
  }, [estimates])

  // Recurring invoices to display (fallback to query response items if state not yet set)
  const displayRecurringInvoices = useMemo(() => {
    const resp: any = recurringInvoicesQuery?.data as any
    const fromQuery = resp?.items
    if (Array.isArray(fromQuery) && fromQuery.length > 0) return fromQuery
    return Array.isArray(recurringInvoices) ? recurringInvoices : []
  }, [recurringInvoicesQuery?.data, recurringInvoices])

  // Calculate sales statistics
  const salesStats = useMemo(() => {
    const safeInvoices = Array.isArray(invoices) ? invoices : [];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Pre-process invoices to ensure consistent data types and handle paid invoices
    const processedInvoices = safeInvoices.map(inv => {
      const status = String(inv.status || '').toLowerCase()
      const totalAmount = Number(inv.totalAmount) || 0
      const balanceDue = Number(inv.balanceDue)
      
      // For paid invoices, balance due is always 0
      if (status === 'paid') {
        return {
          ...inv,
          balanceDue: 0,
          totalAmount,
          status
        }
      }
      
      // For unpaid invoices (sent, draft, pending, etc.), if balanceDue is missing/null/0,
      // it should default to totalAmount since the full amount is still due
      const calculatedBalanceDue = (balanceDue && balanceDue > 0) ? balanceDue : totalAmount
      
      return {
        ...inv,
        balanceDue: calculatedBalanceDue,
        totalAmount,
        status
      }
    });

    // Processed invoices for calculations
    
    // Total invoices amount
    const totalAmount = processedInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    
    // Outstanding invoices (unpaid invoices)
    const outstanding = processedInvoices
      .filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled')
      .reduce((sum, inv) => sum + inv.balanceDue, 0);
    
    // Paid this month
    const paidThisMonth = processedInvoices
      .filter(inv => {
        if (inv.status !== 'paid') return false;
        const paidDate = new Date(inv.issueDate);
        return paidDate.getMonth() === currentMonth && 
               paidDate.getFullYear() === currentYear;
      })
      .reduce((sum, inv) => sum + inv.totalAmount, 0);
    
    // Overdue invoices (past due date and not paid)
    const overdue = processedInvoices
      .filter(inv => {
        if (inv.status?.toLowerCase() === 'paid' || inv.status?.toLowerCase() === 'cancelled') return false;
        if (!inv.dueDate) return false;
        
        const dueDate = new Date(inv.dueDate);
        const hasPastDueDate = isDateBeforeToday(dueDate);
        return (hasPastDueDate && inv.balanceDue > 0) || 
               (inv.status?.toLowerCase() === 'draft' && hasPastDueDate);
      })
      .reduce((sum, inv) => sum + inv.balanceDue, 0);
    
    // Calculate percentages
    const outstandingPercentage = totalAmount > 0 ? (outstanding / totalAmount) * 100 : 0;
    const paidThisMonthPercentage = totalAmount > 0 ? (paidThisMonth / totalAmount) * 100 : 0;
    const overduePercentage = totalAmount > 0 ? (overdue / totalAmount) * 100 : 0;
    
    return { 
      totalAmount,
      outstanding, 
      paidThisMonth, 
      overdue, 
      outstandingPercentage, 
      paidThisMonthPercentage, 
      overduePercentage 
    };
  }, [invoices])

  // Convert currency to Frw when salesStats data is available
  // Note: Invoices may be stored in USD, so we convert from USD to Frw (RWF)
  useEffect(() => {
    const convertToFrw = async () => {
      if (!salesStats) return
      
      setConvertingCurrency(true)
      try {
        // Convert all amounts from USD to Frw (RWF) if they exist
        const amountsToConvert = [
          { value: salesStats.totalAmount || 0, setter: setConvertedTotalAmount },
          { value: salesStats.outstanding || 0, setter: setConvertedOutstanding },
          { value: salesStats.paidThisMonth || 0, setter: setConvertedPaidThisMonth },
          { value: salesStats.overdue || 0, setter: setConvertedOverdue }
        ]

        for (const { value, setter } of amountsToConvert) {
          if (value > 0) {
            try {
              const conversion = await bankingApi.convertCurrency(
                value,
                'USD', // Source currency: USD (invoices may be stored in USD)
                'FRW'  // Target currency: Rwandan Franc (FRW is the ISO code)
              )
              const convertedAmount = conversion?.conversion?.convertedAmount || conversion?.convertedAmount || value
              setter(convertedAmount)
            } catch (error) {
              console.error('Currency conversion error:', error)
              // Fallback to original amount if conversion fails
              setter(value)
            }
          } else {
            setter(0)
          }
        }
      } catch (error) {
        console.error('Currency conversion error:', error)
        // Fallback to original amounts if conversion fails
        setConvertedTotalAmount(salesStats?.totalAmount || 0)
        setConvertedOutstanding(salesStats?.outstanding || 0)
        setConvertedPaidThisMonth(salesStats?.paidThisMonth || 0)
        setConvertedOverdue(salesStats?.overdue || 0)
      } finally {
        setConvertingCurrency(false)
      }
    }

    convertToFrw()
  }, [salesStats])

  if (!mounted) {
    return (
      <PageLayout>
        <div className="p-6 space-y-6">
          <div className="h-6 w-48 bg-muted rounded" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[0,1,2,3].map(i => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="h-10 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </PageLayout>
    )
  }

  if (authLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              <TranslatedText text="Loading..." />
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAuthenticated || authLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-muted-foreground">
              <TranslatedText text="Please log in to access sales features." />
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Quick Stats - Dashboard Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-md transition-all duration-200">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-1.5">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] sm:text-[10px] font-medium text-blue-600 mb-0.5">
                  <TranslatedText text="Outstanding" />
                </p>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <MoonLoader size="sm" color="blue" />
                    <span className="text-xs text-blue-600">
                      <TranslatedText text="Loading..." />
                    </span>
                  </div>
                ) : (
                  <p className="text-base sm:text-lg font-bold text-blue-900 leading-tight">{salesStats.outstandingPercentage.toFixed(0)}%</p>
                )}
                <p className="text-[8px] sm:text-[9px] text-blue-600 mt-0.5">
                  <TranslatedText text="Pending payments" />
                </p>
              </div>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-md flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-md transition-all duration-200">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-1.5">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] sm:text-[10px] font-medium text-green-600 mb-0.5">
                  <TranslatedText text="Paid This Month" />
                </p>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <MoonLoader size="sm" color="green" />
                    <span className="text-xs text-green-600">
                      <TranslatedText text="Loading..." />
                    </span>
                  </div>
                ) : (
                  <p className="text-base sm:text-lg font-bold text-green-900 leading-tight">{salesStats.paidThisMonthPercentage.toFixed(0)}%</p>
                )}
                <p className="text-[8px] sm:text-[9px] text-green-600 mt-0.5">
                  <TranslatedText text="Successful payments" />
                </p>
              </div>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-md transition-all duration-200">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-1.5">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] sm:text-[10px] font-medium text-orange-600 mb-0.5">
                  <TranslatedText text="Overdue" />
                </p>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <MoonLoader size="sm" color="teal" />
                    <span className="text-xs text-orange-600">
                      <TranslatedText text="Loading..." />
                    </span>
                  </div>
                ) : (
                  <p className="text-xs sm:text-sm md:text-base font-bold text-orange-900 leading-tight truncate">
                    {formatCurrency(convertingCurrency ? salesStats.overdue : (convertedOverdue ?? salesStats.overdue), 'Frw')}
                  </p>
                )}
                <p className="text-[8px] sm:text-[9px] text-orange-600 mt-0.5">
                  ({salesStats.overduePercentage.toFixed(0)}% <TranslatedText text="of total" />)
                </p>
              </div>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 hover:shadow-md transition-all duration-200">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-1.5">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] sm:text-[10px] font-medium text-teal-600 mb-0.5">
                  <TranslatedText text="Total Customers" />
                </p>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <MoonLoader size="sm" color="teal" />
                    <span className="text-xs text-teal-600">
                      <TranslatedText text="Loading..." />
                    </span>
                  </div>
                ) : (
                  <p className="text-base sm:text-lg font-bold text-teal-900 leading-tight">{(Array.isArray(customers) ? customers : []).length}</p>
                )}
                <p className="text-[8px] sm:text-[9px] text-teal-600 mt-0.5">
                  <TranslatedText text="Active customers" />
                </p>
              </div>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-teal-500 rounded-md flex items-center justify-center flex-shrink-0">
                <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
 
      {/* Enhanced Navigation */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 mb-8">
        <Tabs defaultValue="invoices" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 bg-slate-50 p-1 rounded-xl h-14">
            <TabsTrigger 
              value="invoices" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <FileText className="w-4 h-4" />
              <span><TranslatedText text="Invoices" /></span>
              <Badge variant="secondary" className="ml-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5">
                {invoices.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="customers" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <Users className="w-4 h-4" />
              <span><TranslatedText text="Customers" /></span>
              <Badge variant="secondary" className="ml-1 bg-green-100 text-green-700 text-xs px-2 py-0.5">
                {customers.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="estimates" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <Calculator className="w-4 h-4" />
              <span><TranslatedText text="Estimates" /></span>
              <Badge variant="secondary" className="ml-1 bg-purple-100 text-purple-700 text-xs px-2 py-0.5">
                {estimates.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="recurring" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" />
              <span><TranslatedText text="Recurring" /></span>
              <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700 text-xs px-2 py-0.5">
                {recurringInvoices.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="approvals" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <CheckCircle className="w-4 h-4" />
              <span><TranslatedText text="Approvals" /></span>
            </TabsTrigger>
            <TabsTrigger 
              value="ai-create" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <MessageSquare className="w-4 h-4" />
              <span><TranslatedText text="AI Create" /></span>
            </TabsTrigger>
            <TabsTrigger 
              value="ai-chat" 
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 font-medium transition-all duration-200 rounded-lg"
            >
              <Bot className="w-4 h-4" />
              <span><TranslatedText text="AI Chat" /></span>
            </TabsTrigger>
          </TabsList>

        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  <TranslatedText text="Recent Invoices" />
                </CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input 
                      placeholder={tSync("Search invoices...", "Search invoices...")} 
                      className="pl-10 w-64" 
                      value={invoiceSearch} 
                      onChange={(e) => setInvoiceSearch(e.target.value)} 
                    />
                  </div>
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    <TranslatedText text="Filter" />
                  </Button>
                  {deletedInvoicesQuery.data && deletedInvoicesQuery.data.length > 0 && (
                    <Button 
                      variant={showDeletedInvoices ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setShowDeletedInvoices(!showDeletedInvoices)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {showDeletedInvoices ? 'Hide' : 'Show'} Deleted ({deletedInvoicesQuery.data.length})
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="text-red-600 text-sm mb-2">{(error as any)?.message || error?.toString() || 'Unknown error'}</div>
              )}
              <div className="space-y-4">
                {(invoicesQuery as any)?.isLoading && (
                  <>
                    {[0,1,2,3,4].map(i => (
                      <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="h-4 w-32 bg-muted rounded" />
                        <div className="flex items-center gap-4">
                          <div className="h-4 w-24 bg-muted rounded" />
                          <div className="h-6 w-20 bg-muted rounded" />
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {!((invoicesQuery as any)?.isLoading) && filteredInvoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="group bg-white border border-slate-200 rounded-2xl hover:shadow-xl hover:border-blue-300 transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                            <span className="text-white font-bold text-xl">#</span>
                          </div>
                          <div className="space-y-1">
                            <p className="font-bold text-slate-900 text-xl">{inv.invoiceNumber}</p>
                            <p className="text-slate-600 font-medium">{(() => {
                              if ((customersQuery as any)?.isLoading) return '—'
                              const c = (Array.isArray(customers) ? customers : []).find(cu => cu.id === inv.customerId)
                              return c ? c.name : inv.customerId
                            })()}</p>
                            <p className="text-sm text-slate-500">
                              <TranslatedText text="Issued" />: {new Date(inv.issueDate).toLocaleDateString()}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge 
                                variant={
                                    inv.status === "paid"
                                    ? "default"
                                      : inv.status === "pending" || inv.status === "sent"
                                      ? "secondary"
                                        : inv.status === "overdue"
                                        ? "destructive"
                                        : "outline"
                                }
                                className={`text-xs px-3 py-1 rounded-full font-medium ${
                                  inv.status === "paid" 
                                    ? "bg-green-100 text-green-700 border-green-200" 
                                    : inv.status === "overdue" 
                                    ? "bg-red-100 text-red-700 border-red-200"
                                    : inv.status === "pending" || inv.status === "sent"
                                    ? "bg-blue-100 text-blue-700 border-blue-200"
                                    : "bg-slate-100 text-slate-700 border-slate-200"
                                }`}
                              >
                                  {inv.status === 'draft' ? <TranslatedText text="Draft" /> :
                                   inv.status === 'paid' ? <TranslatedText text="Paid" /> :
                                   inv.status === 'pending' ? <TranslatedText text="Pending" /> :
                                   inv.status === 'sent' ? <TranslatedText text="Sent" /> :
                                   inv.status === 'overdue' ? <TranslatedText text="Overdue" /> :
                                   inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                              </Badge>
                              {inv.dueDate && (
                                <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                                  <TranslatedText text="Due" />: {new Date(inv.dueDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right space-y-1">
                            {(() => {
                              const customer = (Array.isArray(customers) ? customers : []).find(c => c.id === inv.customerId)
                              const currency = customer?.currency || 'Frw'
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency
                              const converted = convertedInvoiceAmounts[inv.id]
                              const totalAmountToDisplay = currency === 'USD' && converted ? converted.totalAmount : inv.totalAmount
                              const balanceDueToDisplay = currency === 'USD' && converted ? converted.balanceDue : inv.balanceDue
                              
                              return (
                                <>
                                  <p className="font-bold text-slate-900 text-2xl">
                                    {formatCurrency(totalAmountToDisplay, displayCurrency)}
                                  </p>
                                  <p className="text-sm text-slate-600">
                                    <TranslatedText text="Balance" />: {formatCurrency(balanceDueToDisplay, displayCurrency)}
                                  </p>
                                </>
                              )
                            })()}
                            {inv.balanceDue > 0 && (
                              <p className="text-xs text-amber-600 font-medium">
                                {((inv.balanceDue / inv.totalAmount) * 100).toFixed(0)}% <TranslatedText text="remaining" />
                              </p>
                            )}
                          </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          // Only mark as overdue if:
                          // 1. Has a due date in the past AND has balance due
                          // 2. OR is a draft with a due date in the past
                          const hasPastDueDate = inv.dueDate && isDateBeforeToday(new Date(inv.dueDate));
                          const isOverdue = (hasPastDueDate && inv.balanceDue > 0) || 
                                          (inv.status?.toLowerCase() === 'draft' && hasPastDueDate);
                          
                          // Mark as overdue in UI
                          
                          return isOverdue ? (
                            <Badge variant="destructive" className="text-xs">
                              <TranslatedText text="Overdue" />
                            </Badge>
                          ) : null;
                        })()}
                        {inv.status?.toLowerCase() === 'draft' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700" 
                            disabled={markingAsSent === inv.id}
                            onClick={async () => {
                              setMarkingAsSent(inv.id);
                              try { 
                                await apiService.updateInvoice(inv.id, { status: 'sent' }); 
                                await qc.invalidateQueries({ queryKey: ["invoices"] });
                              } catch (error) {
                                toast({
                                  title: 'Error',
                                  description: 'Failed to mark invoice as sent',
                                  variant: 'destructive'
                                });
                              } finally {
                                setMarkingAsSent(null);
                              }
                            }}
                          >
                            {markingAsSent === inv.id ? (
                              <>
                                <MoonLoader size="sm" className="mr-2" color="blue" />
                                Sending...
                              </>
                            ) : (
                              <TranslatedText text="Mark Sent" />
                            )}
                          </Button>
                        )}
                        {inv.status?.toLowerCase() === 'draft' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700" 
                            onClick={() => {
                              // Set the invoice to edit and populate the form
                              setEditingInvoice(inv);
                              setInvoiceDialogOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            <TranslatedText text="Edit" />
                          </Button>
                        )}
                        {inv.status?.toLowerCase() === 'draft' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="hover:bg-red-50 hover:border-red-200 text-red-600 hover:text-red-700" 
                            disabled={deletingInvoice === inv.id}
                            onClick={() => {
                              setInvoiceToDelete({ id: inv.id, invoiceNumber: inv.invoiceNumber });
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            {deletingInvoice === inv.id ? (
                              <>
                                <MoonLoader size="sm" className="mr-2" color="gray" />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4 mr-2" />
                                <TranslatedText text="Delete" />
                              </>
                            )}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => { setCreditInvoiceId(inv.id); setCreditDialogOpen(true) }}>
                          <TranslatedText text="Credit Note" />
                        </Button>
                        {(inv.status?.toLowerCase() === 'sent' || inv.status?.toLowerCase() === 'pending') && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700" 
                              disabled={markingAsPaid === inv.id}
                              onClick={() => handleMarkAsPaid(inv)}
                            >
                              {markingAsPaid === inv.id ? (
                                <>
                                  <MoonLoader size="sm" className="mr-2" color="teal" />
                                  Processing...
                                </>
                              ) : (
                                'Mark Paid'
                              )}
                            </Button>
                            {inv.balanceDue > 0 && (
                              <PaymentButtonCompact
                                invoiceId={inv.id}
                                amount={inv.balanceDue}
                                currency={((customersQuery as any)?.isLoading ? 'Frw' : ((Array.isArray(customers) ? customers : []).find(c => c.id === inv.customerId)?.currency) || 'Frw')}
                                customerEmail={((Array.isArray(customers) ? customers : []).find(c => c.id === inv.customerId)?.email)}
                                customerName={((Array.isArray(customers) ? customers : []).find(c => c.id === inv.customerId)?.name)}
                                description={`Payment for Invoice ${inv.invoiceNumber}`}
                                onPaymentSuccess={async () => {
                                  try {
                                    // Process accounting entries and inventory updates
                                    const accountingResult = await apiService.processInvoicePayment(inv.id);
                                    
                                    toast({
                                      title: 'Payment successful',
                                      description: `Payment processed and accounting entries created. Journal Entry: ${accountingResult.journalEntryId}`,
                                      variant: 'default'
                                    });
                                    
                                    await qc.invalidateQueries({ queryKey: ["invoices"] });
                                    await qc.invalidateQueries({ queryKey: ["journal-entries"] });
                                    await qc.invalidateQueries({ queryKey: ["inventory-movements"] });
                                  } catch (accountingError) {
                                    console.error('Accounting integration error:', accountingError);
                                    
                                    // Check if it's an inventory validation error
                                    if ((accountingError as any).message && (accountingError as any).message.includes('Insufficient inventory')) {
                                      toast({
                                        title: 'Insufficient Inventory',
                                        description: (accountingError as any).message,
                                        variant: 'destructive'
                                      });
                                    } else {
                                      toast({
                                        title: 'Payment successful',
                                        description: 'Payment processed but accounting integration failed. Please check the accounting entries manually.',
                                        variant: 'destructive'
                                      });
                                    }
                                    
                                    await qc.invalidateQueries({ queryKey: ["invoices"] });
                                    await qc.invalidateQueries({ queryKey: ["journal-entries"] });
                                    await qc.invalidateQueries({ queryKey: ["inventory-movements"] });
                                  }
                                }}
                                onPaymentError={(error) => {
                                  console.error('Payment error:', error)
                                }}
                              />
                            )}
                          </>
                        )}
                      </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-10 w-10 p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                              onClick={async () => {
                                try {
                                  setDetailsInvoice(inv)
                                  setDetailsOpen(true)
                                  setDetailsLoading(true)
                                  setDetailsPdfUrl(null)
                                  setDetailsPayLink(null)
                                  setDetailsStatus(inv.status)
                                  setDetailsDueDate((inv.dueDate || '').slice(0,10))
                                  try {
                                    const blob = await apiService.getInvoicePdf(inv.id)
                                    const url = URL.createObjectURL(blob)
                                    setDetailsPdfUrl(url)
                                  } catch {}
                                  try {
                                    const link = await apiService.createPaymentLink(inv.id)
                                    setDetailsPayLink(link.url)
                                  } catch {}
                                  try {
                                    const activity = await apiService.getInvoiceActivity(inv.id)
                                    setDetailsActivity(activity.map((act: any) => ({
                                      id: act.id,
                                      type: act.activityType,
                                      at: act.createdAt,
                                      by: act.performedBy,
                                      message: act.description
                                    })))
                                  } catch { setDetailsActivity([]) }
                                  
                                  // Fetch reconciliation status
                                  try {
                                    const payments = await apiService.getInvoicePayments(inv.id)
                                    // Get payments with bank transaction links
                                    const reconciledPayments = payments.filter((p: any) => p.bankTransactionId || p.isReconciled)
                                    const bankTransactions = reconciledPayments.map((p: any) => ({
                                      id: p.bankTransactionId,
                                      amount: p.amount,
                                      date: p.paymentDate,
                                      description: p.reference || 'Bank Transaction'
                                    }))
                                    
                                    setDetailsReconciliationStatus({
                                      isReconciled: reconciledPayments.length > 0,
                                      reconciledPayments: reconciledPayments.length,
                                      totalPayments: payments.length,
                                      bankTransactions
                                    })
                                  } catch {
                                    setDetailsReconciliationStatus(null)
                                  }
                                } finally {
                                  setDetailsLoading(false)
                                }
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-10 w-10 p-0 hover:bg-purple-50 hover:text-purple-600 transition-colors duration-200"
                              onClick={() => {
                                setSendEmailInvoiceId(inv.id)
                                const c = (Array.isArray(customers) ? customers : []).find(x => x.id === inv.customerId)
                                setSendEmailTo(c?.email || '')
                                setSendEmailOpen(true)
                              }}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress bar for overdue invoices */}
                    {(() => {
                      const hasPastDueDate = inv.dueDate && isDateBeforeToday(new Date(inv.dueDate));
                      const isOverdue = (hasPastDueDate && inv.balanceDue > 0) || 
                                      (inv.status?.toLowerCase() === 'draft' && hasPastDueDate);
                      return isOverdue ? (
                        <div className="h-1 bg-gradient-to-r from-red-500 to-red-600"></div>
                      ) : null;
                    })()}
                  </div>
                ))}
                {filteredInvoices.length === 0 && !loading && (
                  <div className="text-sm text-muted-foreground">No invoices found</div>
                )}
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">Page {invoicePage}</div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={invoicePage <= 1 || loading} onClick={() => setInvoicePage(p => Math.max(1, p - 1))}>Prev</Button>
                  <Button variant="outline" size="sm" disabled={loading || invoices.length < invoicePageSize} onClick={() => setInvoicePage(p => p + 1)}>Next</Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
            <DialogContent className="!max-w-7xl max-h-[95vh] w-[95vw] md:max-h-[90vh] md:w-[90vw] flex flex-col p-0">
              <DialogHeader className="pb-6 border-b border-slate-200 px-6 pt-6 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <DialogTitle className="text-2xl font-bold text-slate-900">
                      {editingInvoice ? <TranslatedText text="Edit Invoice" /> : <TranslatedText text="Create New Invoice" />}
                    </DialogTitle>
                    <DialogDescription className="text-slate-600 mt-1">
                      {editingInvoice ? 'Update the invoice details below' : 'Fill in the details below to create a professional invoice'}
                    </DialogDescription>
                  </div>
                </div>
                {invoiceError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {invoiceError}
                  </div>
                )}
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto min-h-0 px-6 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-6">
                  {/* Left Column - Basic Information */}
                  <div className="md:col-span-1 lg:col-span-1 space-y-6">
                    {/* Customer Selection */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        Customer Information
                      </h3>
                      <div className="space-y-2">
                        <Label htmlFor="inv-customer" className="text-sm font-medium text-slate-700">Select Customer *</Label>
                        <Select value={invoiceForm.customerId} onValueChange={async (newCustomerId) => {
                          const cust = allCustomers.find(c => c.id === newCustomerId)
                          let nextCurrency = invoiceForm.currency
                          if (cust?.currency && autoConvertPrices && !lockRate) {
                            nextCurrency = cust.currency
                          }
                          setInvoiceForm(prev => ({ ...prev, customerId: newCustomerId, currency: nextCurrency }))
                          if (cust?.currency && autoConvertPrices && !lockRate && lastPriceCurrency !== cust.currency) {
                            try {
                              const { rate } = await apiService.getExchangeRate(lastPriceCurrency, cust.currency)
                              const lines = prevLinesConvert(invoiceForm.lines, rate)
                              setInvoiceForm(prev => ({ ...prev, lines, ...recalcTotals(lines), currency: cust.currency || 'Frw' }))
                              setLastPriceCurrency(cust.currency)
                              setExchangeRate(rate)
                            } catch {}
                          }
                        }}>
                          <SelectTrigger className="h-10 border-slate-300 w-full">
                            <SelectValue placeholder="Choose a customer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allCustomers.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{c.name}</span>
                                  {c.currency && (
                                    <span className="text-xs text-slate-500">({c.currency})</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Invoice Details */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Invoice Details
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-number" className="text-sm font-medium text-slate-700">Invoice Number *</Label>
                          <Input 
                            id="inv-number" 
                            value={invoiceForm.invoiceNumber} 
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value })} 
                            placeholder="INV-001" 
                            className="h-10 border-slate-300 w-full"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-currency" className="text-sm font-medium text-slate-700">Currency</Label>
                          <Select value={invoiceForm.currency} onValueChange={async (newCurrency) => {
                            if (autoConvertPrices && !lockRate && lastPriceCurrency !== newCurrency) {
                              try {
                                const { rate } = await apiService.getExchangeRate(lastPriceCurrency, newCurrency)
                                const lines = prevLinesConvert(invoiceForm.lines, rate)
                                setInvoiceForm(prev => ({ ...prev, currency: newCurrency, lines, ...recalcTotals(lines) }))
                                setLastPriceCurrency(newCurrency)
                                setExchangeRate(rate)
                              } catch {
                                setInvoiceForm(prev => ({ ...prev, currency: newCurrency }))
                              }
                            } else {
                              setInvoiceForm(prev => ({ ...prev, currency: newCurrency }))
                            }
                          }}>
                            <SelectTrigger className="h-10 border-slate-300 w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['USD','EUR','GBP','KES','NGN'].map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        Dates
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-issue" className="text-sm font-medium text-slate-700">Issue Date *</Label>
                          <Input 
                            id="inv-issue" 
                            type="date" 
                            value={invoiceForm.issueDate} 
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, issueDate: e.target.value })} 
                            className="h-10 border-slate-300 w-full"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-due" className="text-sm font-medium text-slate-700">Due Date</Label>
                          <Input 
                            id="inv-due" 
                            type="date" 
                            value={invoiceForm.dueDate || ''} 
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} 
                            className="h-10 border-slate-300 w-full"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Pricing & Tax */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-blue-600" />
                        Pricing & Tax
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-shipping" className="text-sm font-medium text-slate-700">Shipping Cost</Label>
                          <Input 
                            id="inv-shipping" 
                            type="number" 
                            step="0.01" 
                            value={invoiceForm.shipping} 
                            onChange={(e) => { 
                              const shipping = parseFloat(e.target.value) || 0; 
                              setInvoiceForm(prev => ({ ...prev, shipping, ...recalcTotals(prev.lines, prev.discount, shipping) })) 
                            }}
                            className="h-10 border-slate-300 w-full"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Discount Mode</Label>
                          <Select value={invoiceForm.discountMode} onValueChange={(dm) => { 
                            setInvoiceForm(prev => ({ ...prev, discountMode: dm as 'amount'|'percent', ...recalcTotals(prev.lines, prev.discount, prev.shipping, prev.taxMode, prev.globalTaxRate, dm as 'amount'|'percent') })) 
                          }}>
                            <SelectTrigger className="h-10 border-slate-300 w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="amount">Amount</SelectItem>
                              <SelectItem value="percent">Percentage</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-discount" className="text-sm font-medium text-slate-700">
                            Discount {invoiceForm.discountMode === 'percent' ? '(%)' : ''}
                          </Label>
                          <Input 
                            id="inv-discount" 
                            type="number" 
                            step="0.01" 
                            value={invoiceForm.discount} 
                            onChange={(e) => { 
                              const discount = parseFloat(e.target.value) || 0; 
                              setInvoiceForm(prev => ({ ...prev, discount, ...recalcTotals(prev.lines, discount) })) 
                            }}
                            className="h-10 border-slate-300 w-full"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Tax Mode</Label>
                          <Select value={invoiceForm.taxMode} onValueChange={(taxMode) => { 
                            setInvoiceForm(prev => ({ ...prev, taxMode: taxMode as 'per_line'|'global', ...recalcTotals(prev.lines, prev.discount, prev.shipping, taxMode as 'per_line'|'global', prev.globalTaxRate) })) 
                          }}>
                            <SelectTrigger className="h-10 border-slate-300 w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="per_line">Per Line Item</SelectItem>
                              <SelectItem value="global">Global Rate</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {invoiceForm.taxMode === 'global' && (
                          <div className="space-y-2">
                            <Label htmlFor="inv-tax" className="text-sm font-medium text-slate-700">Global Tax Rate (%)</Label>
                            <Input 
                              id="inv-tax" 
                              type="number" 
                              step="0.01" 
                              value={invoiceForm.globalTaxRate} 
                              onChange={(e) => { 
                                const r = parseFloat(e.target.value) || 0; 
                                setInvoiceForm(prev => ({ ...prev, globalTaxRate: r, ...recalcTotals(prev.lines, prev.discount, prev.shipping, prev.taxMode, r) })) 
                              }}
                              className="h-10 border-slate-300 w-full"
                              placeholder="0.00"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Notes & Terms */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                        Notes & Terms
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-notes" className="text-sm font-medium text-slate-700">Notes</Label>
                          <Textarea 
                            id="inv-notes" 
                            value={invoiceForm.notes || ''} 
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))} 
                            placeholder="Internal notes or additional information..."
                            className="border-slate-300 min-h-[80px]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-terms" className="text-sm font-medium text-slate-700">Payment Terms</Label>
                          <Textarea 
                            id="inv-terms" 
                            value={invoiceForm.terms || ''} 
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, terms: e.target.value }))} 
                            placeholder="Terms and conditions for payment..."
                            className="border-slate-300 min-h-[80px]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-payment-terms" className="text-sm font-medium text-slate-700">Payment Terms Code</Label>
                          <Select value={invoiceForm.paymentTerms || 'Net 30'} onValueChange={(value) => setInvoiceForm(prev => ({ ...prev, paymentTerms: value }))}>
                            <SelectTrigger className="h-10 border-slate-300 w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                              <SelectItem value="Net 7">Net 7</SelectItem>
                              <SelectItem value="Net 15">Net 15</SelectItem>
                              <SelectItem value="Net 30">Net 30</SelectItem>
                              <SelectItem value="Net 60">Net 60</SelectItem>
                              <SelectItem value="Net 90">Net 90</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Line Items & Totals */}
                  <div className="md:col-span-1 lg:col-span-2 space-y-6">
                    {/* Line Items */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                          <ShoppingCart className="w-5 h-5 text-blue-600" />
                          Line Items
                        </h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const lines = [...invoiceForm.lines, { description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
                            setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Item
                        </Button>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">Add products or services to your invoice</p>
                      
                      <div className="space-y-3">
                        {invoiceForm.lines.map((line, idx) => (
                          <div key={idx} className="grid grid-cols-6 gap-3 p-4 border border-slate-200 rounded-lg bg-white">
                            <div className="col-span-2">
                              <Label className="text-xs text-slate-500">Description</Label>
                              <div className="space-y-1">
                                <Input
                                  placeholder="Item description"
                                  value={line.description}
                                  onChange={(e) => {
                                    const lines = [...invoiceForm.lines]
                                    lines[idx].description = e.target.value
                                    setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                                  }}
                                  className="h-10 border-slate-300"
                                />
                                <Select value={line.productId || ""} onValueChange={(productId) => {
                                  const p = products.find((pr: any) => pr.id === productId)
                                  if (!p) return
                                  const lines = [...invoiceForm.lines]
                                  lines[idx].productId = productId
                                  lines[idx].description = p.name
                                  lines[idx].unitPrice = p.unitPrice || p.price || 0
                                  if (p.isService || p.type === 'SERVICE') {
                                    lines[idx].quantity = 1
                                  }
                                  setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                                }}>
                                  <SelectTrigger className="h-10 border-slate-300">
                                    <SelectValue placeholder="Or select from product catalog..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {products.map((p: any) => (
                                      <SelectItem key={p.id} value={p.id}>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{p.sku ? `${p.sku} - ` : ''}{p.name}</span>
                                          <Badge variant="secondary" className="text-xs">
                                            {formatCurrency(p.unitPrice || p.price || 0, invoiceForm.currency)}
                                          </Badge>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-xs text-slate-500">Qty</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={(function() { const sp = products.find((pr: any) => pr.id === line.productId); return (sp?.isService || sp?.type === 'SERVICE') ? 1 : line.quantity; })()}
                                disabled={(function() { const sp = products.find((pr: any) => pr.id === line.productId); return !!(sp?.isService || sp?.type === 'SERVICE'); })()}
                                onChange={(e) => {
                                  const sp = products.find((pr: any) => pr.id === line.productId)
                                  if (sp?.isService || sp?.type === 'SERVICE') {
                                    return
                                  }
                                  const lines = [...invoiceForm.lines]
                                  lines[idx].quantity = parseFloat(e.target.value) || 0
                                  setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                                }}
                                className="h-10 border-slate-300"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-xs text-slate-500">Unit Price</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={line.unitPrice}
                                onChange={(e) => {
                                  const lines = [...invoiceForm.lines]
                                  lines[idx].unitPrice = parseFloat(e.target.value) || 0
                                  setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                                }}
                                className="h-10 border-slate-300"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-xs text-slate-500">Discount</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={line.lineDiscount || 0}
                                onChange={(e) => {
                                  const lines = [...invoiceForm.lines]
                                  lines[idx].lineDiscount = parseFloat(e.target.value) || 0
                                  setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                                }}
                                className="h-10 border-slate-300"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-xs text-slate-500">Tax %</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={line.taxRate}
                                disabled={invoiceForm.taxMode === 'global'} 
                                onChange={(e) => {
                                  const lines = [...invoiceForm.lines]
                                  lines[idx].taxRate = parseFloat(e.target.value) || 0
                                  setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                                }}
                                className="h-10 border-slate-300"
                              />
                              <div className="text-xs text-slate-400 mt-1">
                                {invoiceForm.taxMode === 'global' ? 'global' : 'tax %'}
                              </div>
                            </div>
                            
                            <div className="flex items-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const lines = invoiceForm.lines.filter((_, i) => i !== idx)
                                  setInvoiceForm({ ...invoiceForm, lines: lines.length ? lines : [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }], ...recalcTotals(lines.length ? lines : [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]) })
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div className="col-span-6 mt-2">
                              <div className="text-sm text-slate-600">
                                Line Total: {formatCurrency((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) - (Number(line.lineDiscount) || 0), invoiceForm.currency)}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {invoiceForm.lines.length === 0 && (
                          <div className="text-center py-8 text-slate-500">
                            <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                            <p>No line items added yet</p>
                            <Button
                              variant="outline"
                              className="mt-2"
                              onClick={() => {
                                const lines = [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
                                setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add First Item
                            </Button>
                          </div>
                        )}
                        
                        {invoiceForm.lines.length > 0 && (
                          <div className="flex items-center justify-between text-sm text-slate-600">
                            <span>
                              <span className="font-medium">{invoiceForm.lines.length}</span> line item{invoiceForm.lines.length !== 1 ? 's' : ''} added
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const lines = [...invoiceForm.lines, { description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
                                setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Another Item
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Invoice Summary */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-blue-600" />
                        Invoice Summary
                      </h3>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Subtotal:</span>
                          <span className="text-slate-900 font-semibold text-lg">{formatCurrency(invoiceForm.subtotal, invoiceForm.currency)}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Tax:</span>
                          <span className="text-slate-900 font-semibold text-lg">{formatCurrency(invoiceForm.taxTotal, invoiceForm.currency)}</span>
                        </div>
                        
                        {(Number(invoiceForm.shipping) || 0) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600">Shipping:</span>
                            <span className="text-slate-900 font-semibold text-lg">{formatCurrency(invoiceForm.shipping, invoiceForm.currency)}</span>
                          </div>
                        )}
                        
                        {(Number(invoiceForm.discount) || 0) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600">Discount:</span>
                            <span className="text-red-600 font-semibold text-lg">-{formatCurrency(invoiceForm.discount, invoiceForm.currency)}</span>
                          </div>
                        )}
                        
                        <div className="border-t border-slate-200 pt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-900 font-bold text-lg">Total:</span>
                            <span className="text-slate-900 font-bold text-2xl">{formatCurrency(invoiceForm.totalAmount, invoiceForm.currency)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Exchange Rate Info */}
                      {exchangeRate !== 1 && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-blue-700">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span>Exchange Rate: {exchangeRate.toFixed(4)} {lastPriceCurrency} → {invoiceForm.currency} {lockRate ? '(locked)' : ''}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="pt-6 border-t border-slate-200 px-6 pb-6 flex-shrink-0">
                <div className="flex items-center justify-between w-full">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setInvoiceDialogOpen(false)
                        setEditingInvoice(null)
                      }} 
                      disabled={invoiceSaving}
                    >
                      Cancel
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={async () => {
                      try {
                        setInvoiceSaving(true)
                        setInvoiceError(null)
                        if (!invoiceForm.customerId) throw new Error('Customer is required')
                        if (!invoiceForm.invoiceNumber) throw new Error('Invoice number is required')
                        const payload = {
                          companyId: selectedCompany,
                          customerId: invoiceForm.customerId,
                          invoiceNumber: invoiceForm.invoiceNumber,
                          issueDate: invoiceForm.issueDate,
                          dueDate: invoiceForm.dueDate || undefined,
                          currency: invoiceForm.currency,
                          lines: invoiceForm.lines.map(l => ({ 
                            description: l.description, 
                            quantity: l.quantity, 
                            unitPrice: l.unitPrice, 
                            taxRate: l.taxRate, 
                            lineDiscount: l.lineDiscount || 0, 
                            productId: l.productId || null 
                          })),
                          // Backend model fields
                          subtotal: invoiceForm.subtotal,
                          taxAmount: invoiceForm.taxTotal,
                          discountAmount: invoiceForm.discount,
                          shippingAmount: invoiceForm.shipping,
                          totalAmount: invoiceForm.totalAmount,
                          balanceDue: invoiceForm.totalAmount,
                          // Optional fields from form
                          notes: invoiceForm.notes || '',
                          terms: invoiceForm.terms || 'Payment due within 30 days',
                          footer: '',
                          paymentTerms: invoiceForm.paymentTerms || 'Net 30',
                          lateFeeRate: null,
                          deliveryMethod: 'email',
                          taxInclusive: false,
                          taxExemptionReason: null,
                          createdBy: null,
                          status: 'draft',
                          // Keep custom fields for potential future use
                          fxRateSnapshot: exchangeRate,
                          totalsSnapshot: {
                            subtotal: invoiceForm.subtotal,
                            taxTotal: invoiceForm.taxTotal,
                            shipping: invoiceForm.shipping,
                            discountMode: invoiceForm.discountMode,
                            discount: invoiceForm.discount,
                            total: invoiceForm.totalAmount
                          }
                        }
                        let result;
                        if (editingInvoice) {
                          // Update existing invoice
                          result = await apiService.updateInvoice(editingInvoice.id, payload as any)
                          toast({
                            title: 'Success',
                            description: 'Invoice updated successfully',
                            variant: 'default'
                          })
                        } else {
                          // Create new invoice
                          result = await apiService.createInvoice(payload as any)
                          // immediately enable PDF/Payment actions via created.id
                          if (result?.id) {
                            try {
                              await generateInvoicePDF(result, 'preview')
                            } catch {}
                            try {
                              const link = await apiService.createPaymentLink(result.id, { expiresInMinutes: 60 })
                              await navigator.clipboard.writeText(link.url)
                              toast({ title: 'Payment link copied', description: link.url })
                            } catch {}
                          }
                        }
                        await qc.invalidateQueries({ queryKey: ["invoices"] })
                        setInvoiceDialogOpen(false)
                        setEditingInvoice(null)
                      } catch (e: any) {
                        setInvoiceError((e as any)?.message || (editingInvoice ? 'Failed to update invoice' : 'Failed to create invoice'))
                      } finally {
                        setInvoiceSaving(false)
                      }
                    }} 
                    disabled={invoiceSaving}
                    className="h-12 px-8 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    {invoiceSaving ? (
                      <>
                        <MoonLoader size="sm" className="mr-2" color="teal" />
                        {editingInvoice ? 'Updating Invoice...' : 'Creating Invoice...'}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        {editingInvoice ? <TranslatedText text="Update Invoice" /> : <TranslatedText text="Create Invoice" />}
                      </>
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </TabsContent>

        {/* Deleted Invoices Section */}
        {showDeletedInvoices && deletedInvoicesQuery.data && deletedInvoicesQuery.data.length > 0 && (
          <Card className="mt-4 border-red-200">
            <CardHeader>
              <CardTitle className="text-red-600">Deleted Invoices</CardTitle>
              <CardDescription>Invoices that have been deleted but can be restored</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {deletedInvoicesQuery.data.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-red-600 border-red-300">
                          {inv.status}
                        </Badge>
                        <span className="font-medium">{inv.invoiceNumber}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {inv.customerId && (() => {
                          const c = (Array.isArray(customers) ? customers : []).find(cu => cu.id === inv.customerId)
                          return c?.name || 'Unknown Customer'
                        })()}
                      </div>
                      <div className="text-sm text-gray-600">
                        ${typeof inv.totalAmount === 'number' ? inv.totalAmount.toFixed(2) : '0.00'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString() : 'No date'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Deleted: {inv.deletedAt ? new Date(inv.deletedAt).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="hover:bg-green-50 hover:border-green-200 text-green-600" 
                        onClick={() => handleRestoreInvoice(inv.id)}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <TabsContent value="customers" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Customer Directory</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input 
                      placeholder="Search customers..." 
                      className="pl-10 w-64" 
                      value={customerSearch} 
                      onChange={(e) => {
                        setCustomerSearch(e.target.value)
                        setCustomerPage(1) // Reset to first page when searching
                      }} 
                    />
                  </div>
                  <Button onClick={() => { setEditingCustomer(null); setCustomerForm({ name: "", email: "", phone: "", tin: "", currency: 'Frw' }); setCustomerDialogOpen(true) }}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Add Customer" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(Array.isArray(filteredCustomers) ? filteredCustomers : []).map((customer: CustomerListItem) => (
                  <div
                    key={customer.id}
                    className="group flex items-center justify-between p-6 bg-white border border-gray-100 rounded-xl hover:shadow-lg hover:border-blue-200 transition-all duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center group-hover:from-green-200 group-hover:to-green-300 transition-all duration-200">
                        <span className="text-green-600 font-bold text-lg">{customer.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-lg">{customer.name}</p>
                        <p className="text-sm text-gray-600">{customer.phone || '—'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {customer.currency || 'Frw'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="hover:bg-green-50 hover:border-green-200 hover:text-green-700 group text-gray-700" 
                          title="View Customer"
                          onClick={() => { setEditingCustomer(customer); setCustomerForm({ name: customer.name, email: customer.email, phone: customer.phone, tin: customer.taxNumber, currency: customer.currency || 'Frw' }); setCustomerDialogOpen(true) }}
                        >
                          <Eye className="w-4 h-4 text-gray-700 group-hover:text-green-700" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 group text-gray-700"
                          title="Edit Customer"
                          onClick={() => { 
                            setEditingCustomer(customer); 
                            setCustomerForm({ 
                              name: customer.name, 
                              email: customer.email || '', 
                              currency: customer.currency || 'Frw' 
                            }); 
                            setCustomerDialogOpen(true) 
                          }}
                        >
                          <Edit className="w-4 h-4 text-gray-700 group-hover:text-blue-700" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredCustomers.length === 0 && !loading && (
                  <div className="text-sm text-muted-foreground">No customers found</div>
                )}
                {customersQuery.data && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Showing {((customerPage - 1) * customerPageSize) + 1} to {Math.min(customerPage * customerPageSize, customersQuery.data.total)} of {customersQuery.data.total} customers
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        className="text-sm border rounded px-2 py-1"
                        value={customerPageSize}
                        onChange={(e) => {
                          setCustomerPageSize(Number(e.target.value))
                          setCustomerPage(1) // Reset to first page when changing page size
                        }}
                      >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomerPage(prev => Math.max(1, prev - 1))}
                        disabled={!customersQuery.data.hasPrev || customersQuery.isLoading}
                      >
                        Previous
                      </Button>
                      <div className="text-sm">
                        Page {customerPage} of {customersQuery.data.totalPages}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomerPage(prev => prev + 1)}
                        disabled={!customersQuery.data.hasNext || customersQuery.isLoading}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCustomer ? <TranslatedText text="Edit Customer" /> : <TranslatedText text="Add Customer" />}</DialogTitle>
                <DialogDescription>Enter customer details below.</DialogDescription>
              </DialogHeader>
              {customerError && (
                <div className="text-red-600 text-sm">{customerError}</div>
              )}
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="cust-name">Name <span className="text-red-500">*</span></Label>
                  <Input 
                    id="cust-name" 
                    value={customerForm.name} 
                    onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cust-phone">Phone <span className="text-red-500">*</span></Label>
                  <Input 
                    id="cust-phone" 
                    type="tel" 
                    value={customerForm.phone || ''} 
                    onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} 
                    placeholder="e.g., +1234567890"
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cust-tin">TIN (Tax Identification Number) <span className="text-red-500">*</span></Label>
                  <Input 
                    id="cust-tin" 
                    value={customerForm.tin || ''} 
                    onChange={(e) => setCustomerForm({ ...customerForm, tin: e.target.value })} 
                    placeholder="e.g., 123456789"
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cust-email">Email (Optional)</Label>
                  <Input 
                    id="cust-email" 
                    type="email" 
                    value={customerForm.email || ''} 
                    onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} 
                    placeholder="customer@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cust-currency">Currency</Label>
                  <select 
                    id="cust-currency" 
                    className="w-full border rounded px-2 py-2" 
                    value={customerForm.currency || 'Frw'} 
                    onChange={(e) => setCustomerForm({ ...customerForm, currency: e.target.value })}
                  >
                    {['Frw','USD','EUR','GBP','KES','NGN'].map(c => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCustomerDialogOpen(false)} disabled={customerSaving}>Cancel</Button>
                <Button onClick={async () => {
                  try {
                    setCustomerSaving(true)
                    setCustomerError(null)
                    if (!customerForm.name || customerForm.name.trim() === '') {
                      throw new Error('Name is required')
                    }
                    if (!customerForm.phone || customerForm.phone.trim() === '') {
                      throw new Error('Phone is required')
                    }
                    if (!customerForm.tin || customerForm.tin.trim() === '') {
                      throw new Error('TIN (Tax Identification Number) is required')
                    }
                    if (!selectedCompany) throw new Error('No company selected')
                    if (editingCustomer) {
                      await apiService.updateCustomer(editingCustomer.id, { 
                        companyId: selectedCompany, 
                        name: customerForm.name, 
                        email: customerForm.email, 
                        phone: customerForm.phone,
                        taxNumber: customerForm.tin,
                        currency: customerForm.currency 
                      })
                      toast({ title: 'Customer updated', description: `${customerForm.name}` })
                    } else {
                      await apiService.createCustomer({ 
                        companyId: selectedCompany, 
                        name: customerForm.name, 
                        email: customerForm.email, 
                        phone: customerForm.phone,
                        taxNumber: customerForm.tin,
                        currency: customerForm.currency 
                      })
                      toast({ title: 'Customer created', description: `${customerForm.name}` })
                    }
                    await qc.invalidateQueries({ queryKey: ["customers"] })
                    setCustomerDialogOpen(false)
                  } catch (e: any) {
                    setCustomerError((e as any)?.message || 'Failed to save customer')
                    toast({ title: 'Customer save failed', description: (e as any)?.message || 'Failed to save customer', variant: 'destructive' })
                  } finally {
                    setCustomerSaving(false)
                  }
                }} disabled={customerSaving}>
                  {customerSaving ? 'Saving…' : (editingCustomer ? 'Save Changes' : 'Create Customer')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="estimates" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
              <CardTitle>Estimates & Quotes</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input 
                      placeholder="Search estimates..." 
                      className="pl-10 w-64" 
                      value={estimateSearch} 
                      onChange={(e) => {
                        setEstimateSearch(e.target.value)

                      }} 
                    />
                  </div>
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    Filter
                  </Button>
                  <Button onClick={() => {
                    const list = Array.isArray(allCustomers) ? allCustomers : []
                    const defaultCustomerId = list[0]?.id || ""
                    const defaultCurrency = list.find(c => c.id === defaultCustomerId)?.currency || 'Frw'
                    setEstimateForm({
                      customerId: defaultCustomerId,
                      estimateNumber: '',
                      issueDate: new Date().toISOString().slice(0,10),
                      expiryDate: '',
                      currency: defaultCurrency,
                      notes: '',
                      terms: '',
                      lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }]
                    })
                    setEstimateError(null)
                    setEstimateDialogOpen(true)
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Estimate
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="text-red-600 text-sm mb-2">{(error as any)?.message || error?.toString() || 'Unknown error'}</div>
              )}
              <div className="space-y-4">
                {(estimatesQuery as any)?.isLoading && (
                  <>
                    {[0,1,2,3,4].map(i => (
                      <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="h-4 w-32 bg-muted rounded" />
                        <div className="flex items-center gap-4">
                          <div className="h-4 w-24 bg-muted rounded" />
                          <div className="h-6 w-20 bg-muted rounded" />
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {!((estimatesQuery as any)?.isLoading) && displayEstimates.map((est) => (
                  <div
                    key={est.id}
                    className="group flex items-center justify-between p-6 bg-white border border-gray-100 rounded-xl hover:shadow-lg hover:border-blue-200 transition-all duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center group-hover:from-purple-200 group-hover:to-purple-300 transition-all duration-200">
                        <span className="text-purple-600 font-bold text-lg">#</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-lg">{est.estimateNumber}</p>
                        <p className="text-sm text-gray-600">{est.customer?.name || 'Customer'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant={
                              est.status === "accepted"
                                ? "default"
                                : est.status === "sent"
                                  ? "secondary"
                                  : est.status === "rejected"
                                    ? "destructive"
                                    : "outline"
                            }
                            className="text-xs"
                          >
                            {est.status}
                          </Badge>
                          {est.issueDate && (
                            <span className="text-xs text-gray-500">
                              {new Date(est.issueDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg text-gray-900">
                          {formatCurrency(est.totalAmount, est.currency)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {est.currency}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {est.status?.toLowerCase() === 'draft' && (
                          <Button variant="outline" size="sm" className="hover:bg-blue-50 hover:border-blue-200" onClick={async () => {
                            try { await apiService.updateEstimate(est.id, { status: 'sent' }); await qc.invalidateQueries({ queryKey: ["estimates"] }) } catch {}
                          }}>Mark Sent</Button>
                        )}
                        {est.status?.toLowerCase() === 'sent' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                            try { await apiService.updateEstimate(est.id, { status: 'accepted' }); await qc.invalidateQueries({ queryKey: ["estimates"] }) } catch {}
                          }}>Mark Accepted</Button>
                        )}
                        {est.status?.toLowerCase() === 'accepted' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={async () => {
                            try { 
                              const invoice = await apiService.convertEstimateToInvoice(est.id)
                              await qc.invalidateQueries({ queryKey: ["estimates"] })
                              await qc.invalidateQueries({ queryKey: ["invoices"] })
                              // TODO: Show success message or redirect to invoice
                            } catch {}
                          }}>Convert to Invoice</Button>
                        )}
                      </div>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Button variant="ghost" size="sm" onClick={async () => {
                          try {
                            const pdf = await apiService.getEstimatePdf(est.id)
                            const url = URL.createObjectURL(pdf)
                            window.open(url, '_blank')
                          } catch {}
                        }}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={async () => {
                          try {
                            setEstimateViewLoading(true)
                            setSelectedEstimate(est)
                            setEstimateViewDialogOpen(true)
                          } finally {
                            setEstimateViewLoading(false)
                          }
                        }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={async () => {
                          try {
                            setSelectedEstimate(est)
                            
                            // Populate edit form with current estimate data
                            setEstimateEditForm({
                              customerId: est.customerId || "",
                              estimateNumber: est.estimateNumber || "",
                              issueDate: formatDateForInput(est.issueDate) || new Date().toISOString().slice(0,10),
                              expiryDate: formatDateForInput(est.expiryDate),
                              currency: est.currency || 'Frw',
                              notes: est.notes || "",
                              terms: est.terms || "",
                              lines: est.lines?.map((line: any) => ({
                                description: line.description || "",
                                quantity: line.quantity || 1,
                                unitPrice: line.unitPrice || 0,
                                taxRate: line.taxRate || 0
                              })) || [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }]
                            })
                            setEstimateEditDialogOpen(true)
                          } catch (error) {
                            console.error('Error preparing estimate for edit:', error)
                          }
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {displayEstimates.length === 0 && !loading && (
                  <div className="text-sm text-muted-foreground">No estimates found</div>
                )}
                {estimatesQuery.data && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Showing {displayEstimates.length} estimates
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create Estimate Modal */}
        <Dialog open={estimateDialogOpen} onOpenChange={setEstimateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto z-50">
            <DialogHeader>
              <DialogTitle>Create Estimate</DialogTitle>
              <DialogDescription>Fill in estimate details.</DialogDescription>
            </DialogHeader>
            {estimateError && (
              <div className="text-red-600 text-sm mb-2">{estimateError}</div>
            )}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Customer</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={estimateForm.customerId}
                    onChange={(e) => {
                      const cid = e.target.value
                      const cust = (Array.isArray(allCustomers) ? allCustomers : []).find((c: any) => c.id === cid)
                      setEstimateForm(prev => ({ ...prev, customerId: cid, currency: cust?.currency || prev.currency }))
                    }}
                  >
                    <option value="">Select customer</option>
                    {(Array.isArray(allCustomers) ? allCustomers : []).map((c: any) => (
                      <option key={c.id} value={c.id}>{c.name} {c.email ? `(${c.email})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Estimate Number</label>
                  <Input
                    placeholder="e.g. EST-2025-0001"
                    value={estimateForm.estimateNumber}
                    onChange={(e) => setEstimateForm(prev => ({ ...prev, estimateNumber: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Issue Date</label>
                  <Input type="date" value={estimateForm.issueDate} onChange={(e) => setEstimateForm(prev => ({ ...prev, issueDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiry Date</label>
                  <Input type="date" value={estimateForm.expiryDate || ''} onChange={(e) => setEstimateForm(prev => ({ ...prev, expiryDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <Input value={estimateForm.currency} onChange={(e) => setEstimateForm(prev => ({ ...prev, currency: e.target.value.toUpperCase().slice(0,3) }))} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Line Items</label>
                <div className="space-y-3">
                  {estimateForm.lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                      <div className="md:col-span-5">
                        <Input placeholder="Description" value={line.description} onChange={(e) => {
                          const lines = [...estimateForm.lines]
                          lines[idx] = { ...lines[idx], description: e.target.value }
                          setEstimateForm(prev => ({ ...prev, lines }))
                        }} />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="number" min={0} step={1} placeholder="Qty" value={line.quantity}
                          onChange={(e) => {
                            const lines = [...estimateForm.lines]
                            lines[idx] = { ...lines[idx], quantity: Number(e.target.value) }
                            setEstimateForm(prev => ({ ...prev, lines }))
                          }} />
                      </div>
                      <div className="md:col-span-3">
                        <Input type="number" min={0} step={0.01} placeholder="Unit Price" value={line.unitPrice}
                          onChange={(e) => {
                            const lines = [...estimateForm.lines]
                            lines[idx] = { ...lines[idx], unitPrice: Number(e.target.value) }
                            setEstimateForm(prev => ({ ...prev, lines }))
                          }} />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="number" min={0} max={100} step={0.01} placeholder="Tax %" value={line.taxRate}
                          onChange={(e) => {
                            const lines = [...estimateForm.lines]
                            lines[idx] = { ...lines[idx], taxRate: Number(e.target.value) }
                            setEstimateForm(prev => ({ ...prev, lines }))
                          }} />
                      </div>
                    </div>
                  ))}
                  <div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setEstimateForm(prev => ({ ...prev, lines: [...prev.lines, { description: "", quantity: 1, unitPrice: 0, taxRate: 0 }] }))}>Add Line</Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea className="w-full border rounded px-3 py-2 min-h-[80px]" value={estimateForm.notes}
                    onChange={(e) => setEstimateForm(prev => ({ ...prev, notes: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Terms</label>
                  <textarea className="w-full border rounded px-3 py-2 min-h-[80px]" value={estimateForm.terms}
                    onChange={(e) => setEstimateForm(prev => ({ ...prev, terms: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter className="sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEstimateDialogOpen(false)} disabled={estimateSaving}>Cancel</Button>
              <Button onClick={async () => {
                try {
                  setEstimateSaving(true)
                  setEstimateError(null)
                  if (!selectedCompany) throw new Error('No company selected')
                  if (!estimateForm.customerId) throw new Error('Customer is required')
                  if (!estimateForm.estimateNumber) throw new Error('Estimate number is required')
                  if (!estimateForm.issueDate) throw new Error('Issue date is required')

                  const payload: any = {
                    companyId: selectedCompany,
                    customerId: estimateForm.customerId,
                    estimateNumber: estimateForm.estimateNumber,
                    issueDate: estimateForm.issueDate,
                    expiryDate: estimateForm.expiryDate || undefined,
                    currency: estimateForm.currency || 'Frw',
                    notes: estimateForm.notes || undefined,
                    terms: estimateForm.terms || undefined,
                    lines: estimateForm.lines.map(l => ({
                      description: l.description,
                      quantity: Number(l.quantity) || 0,
                      unitPrice: Number(l.unitPrice) || 0,
                      taxRate: Number(l.taxRate) || 0,
                    }))
                  }

                  await apiService.createEstimate(payload)
                  await qc.invalidateQueries({ queryKey: ["estimates"] })
                  toast({ title: 'Estimate created', description: payload.estimateNumber })
                  setEstimateDialogOpen(false)
                } catch (e: any) {
                  setEstimateError((e as any)?.message || 'Failed to create estimate')
                  toast({ title: 'Estimate creation failed', description: (e as any)?.message || 'Failed to create estimate', variant: 'destructive' })
                } finally {
                  setEstimateSaving(false)
                }
              }} disabled={estimateSaving}>
                {estimateSaving ? 'Creating…' : 'Create Estimate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Estimate Modal */}
        <Dialog open={estimateViewDialogOpen} onOpenChange={setEstimateViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-50">
            <DialogHeader>
              <DialogTitle>Estimate Details</DialogTitle>
              <DialogDescription>View estimate information and details.</DialogDescription>
            </DialogHeader>
            {selectedEstimate && (
              <div className="space-y-6">
                {/* Header Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Estimate Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Estimate Number</label>
                        <p className="text-lg font-semibold">{selectedEstimate.estimateNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Customer</label>
                        <p className="text-lg">{selectedEstimate.customer?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Status</label>
                        <Badge
                          variant={
                            selectedEstimate.status === "accepted"
                              ? "default"
                              : selectedEstimate.status === "sent"
                                ? "secondary"
                                : selectedEstimate.status === "rejected"
                                  ? "destructive"
                                  : "outline"
                          }
                          className="text-sm"
                        >
                          {selectedEstimate.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Dates & Amounts</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Issue Date</label>
                        <p>{selectedEstimate.issueDate ? new Date(selectedEstimate.issueDate).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Expiry Date</label>
                        <p>{selectedEstimate.expiryDate ? new Date(selectedEstimate.expiryDate).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Total Amount</label>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(selectedEstimate.totalAmount, selectedEstimate.currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Line Items</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-200 px-4 py-2 text-left">Description</th>
                          <th className="border border-gray-200 px-4 py-2 text-right">Qty</th>
                          <th className="border border-gray-200 px-4 py-2 text-right">Unit Price</th>
                          <th className="border border-gray-200 px-4 py-2 text-right">Tax Rate</th>
                          <th className="border border-gray-200 px-4 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEstimate.lines?.map((line: any, index: number) => (
                          <tr key={index}>
                            <td className="border border-gray-200 px-4 py-2">{line.description || '-'}</td>
                            <td className="border border-gray-200 px-4 py-2 text-right">{line.quantity || 0}</td>
                            <td className="border border-gray-200 px-4 py-2 text-right">{formatCurrency(line.unitPrice || 0, selectedEstimate.currency)}</td>
                            <td className="border border-gray-200 px-4 py-2 text-right">{line.taxRate || 0}%</td>
                            <td className="border border-gray-200 px-4 py-2 text-right">{formatCurrency(line.lineTotal || 0, selectedEstimate.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Notes and Terms */}
                {(selectedEstimate.notes || selectedEstimate.terms) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {selectedEstimate.notes && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Notes</h3>
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedEstimate.notes}</p>
                      </div>
                    )}
                    {selectedEstimate.terms && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Terms</h3>
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedEstimate.terms}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEstimateViewDialogOpen(false)}>Close</Button>
              <Button onClick={() => {
                if (selectedEstimate) {
                  // Switch to edit mode
                  setEstimateEditForm({
                    customerId: selectedEstimate.customerId || "",
                    estimateNumber: selectedEstimate.estimateNumber || "",
                    issueDate: formatDateForInput(selectedEstimate.issueDate) || new Date().toISOString().slice(0,10),
                    expiryDate: formatDateForInput(selectedEstimate.expiryDate),
                    currency: selectedEstimate.currency || 'Frw',
                    notes: selectedEstimate.notes || "",
                    terms: selectedEstimate.terms || "",
                    lines: selectedEstimate.lines?.map((line: any) => ({
                      description: line.description || "",
                      quantity: line.quantity || 1,
                      unitPrice: line.unitPrice || 0,
                      taxRate: line.taxRate || 0
                    })) || [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0 }]
                  })
                  setEstimateViewDialogOpen(false)
                  setEstimateEditDialogOpen(true)
                }
              }}>Edit Estimate</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Estimate Modal */}
        <Dialog open={estimateEditDialogOpen} onOpenChange={setEstimateEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto z-50">
            <DialogHeader>
              <DialogTitle>Edit Estimate</DialogTitle>
              <DialogDescription>Update estimate details and information.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {estimateError && (
                <div className="text-red-600 text-sm mb-2">{estimateError}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Customer</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={estimateEditForm.customerId}
                    onChange={(e) => {
                      const cid = e.target.value
                      const cust = (Array.isArray(allCustomers) ? allCustomers : []).find((c: any) => c.id === cid)
                      setEstimateEditForm(prev => ({ ...prev, customerId: cid, currency: cust?.currency || prev.currency }))
                    }}
                  >
                    <option value="">Select customer</option>
                    {(Array.isArray(allCustomers) ? allCustomers : []).map((cust: any) => (
                      <option key={cust.id} value={cust.id}>{cust.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Estimate Number</label>
                  <Input
                    placeholder="e.g. EST-2025-0001"
                    value={estimateEditForm.estimateNumber}
                    onChange={(e) => setEstimateEditForm(prev => ({ ...prev, estimateNumber: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Issue Date</label>
                  <Input type="date" value={estimateEditForm.issueDate} onChange={(e) => setEstimateEditForm(prev => ({ ...prev, issueDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiry Date</label>
                  <Input type="date" value={estimateEditForm.expiryDate || ''} onChange={(e) => setEstimateEditForm(prev => ({ ...prev, expiryDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <Input value={estimateEditForm.currency} onChange={(e) => setEstimateEditForm(prev => ({ ...prev, currency: e.target.value.toUpperCase().slice(0,3) }))} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Line Items</label>
                <div className="space-y-3">
                  {estimateEditForm.lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                      <div className="md:col-span-5">
                        <Input placeholder="Description" value={line.description} onChange={(e) => {
                          const lines = [...estimateEditForm.lines]
                          lines[idx] = { ...lines[idx], description: e.target.value }
                          setEstimateEditForm(prev => ({ ...prev, lines }))
                        }} />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="number" min={0} step={1} placeholder="Qty" value={line.quantity}
                          onChange={(e) => {
                            const lines = [...estimateEditForm.lines]
                            lines[idx] = { ...lines[idx], quantity: Number(e.target.value) }
                            setEstimateEditForm(prev => ({ ...prev, lines }))
                          }} />
                      </div>
                      <div className="md:col-span-3">
                        <Input type="number" min={0} step={0.01} placeholder="Unit Price" value={line.unitPrice}
                          onChange={(e) => {
                            const lines = [...estimateEditForm.lines]
                            lines[idx] = { ...lines[idx], unitPrice: Number(e.target.value) }
                            setEstimateEditForm(prev => ({ ...prev, lines }))
                          }} />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="number" min={0} max={100} step={0.01} placeholder="Tax %" value={line.taxRate}
                          onChange={(e) => {
                            const lines = [...estimateEditForm.lines]
                            lines[idx] = { ...lines[idx], taxRate: Number(e.target.value) }
                            setEstimateEditForm(prev => ({ ...prev, lines }))
                          }} />
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={() => {
                    setEstimateEditForm(prev => ({ ...prev, lines: [...prev.lines, { description: "", quantity: 1, unitPrice: 0, taxRate: 0 }] }))
                  }}>Add Line Item</Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea className="w-full border rounded px-3 py-2 min-h-[80px]" value={estimateEditForm.notes}
                    onChange={(e) => setEstimateEditForm(prev => ({ ...prev, notes: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Terms</label>
                  <textarea className="w-full border rounded px-3 py-2 min-h-[80px]" value={estimateEditForm.terms}
                    onChange={(e) => setEstimateEditForm(prev => ({ ...prev, terms: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter className="sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEstimateEditDialogOpen(false)} disabled={estimateSaving}>Cancel</Button>
              <Button onClick={async () => {
                try {
                  setEstimateSaving(true)
                  setEstimateError(null)
                  if (!selectedEstimate) throw new Error('No estimate selected')
                  if (!estimateEditForm.customerId) throw new Error('Customer is required')
                  if (!estimateEditForm.estimateNumber) throw new Error('Estimate number is required')
                  if (!estimateEditForm.issueDate) throw new Error('Issue date is required')

                  const payload: any = {
                    customerId: estimateEditForm.customerId,
                    estimateNumber: estimateEditForm.estimateNumber,
                    issueDate: estimateEditForm.issueDate,
                    expiryDate: estimateEditForm.expiryDate || undefined,
                    currency: estimateEditForm.currency || 'Frw',
                    notes: estimateEditForm.notes || undefined,
                    terms: estimateEditForm.terms || undefined,
                    lines: estimateEditForm.lines.map(l => ({
                      description: l.description,
                      quantity: Number(l.quantity) || 0,
                      unitPrice: Number(l.unitPrice) || 0,
                      taxRate: Number(l.taxRate) || 0,
                    }))
                  }

                  await apiService.updateEstimate(selectedEstimate.id, payload)
                  await qc.invalidateQueries({ queryKey: ["estimates"] })
                  toast({ title: 'Estimate updated', description: payload.estimateNumber })
                  setEstimateEditDialogOpen(false)
                  setEstimateError(null)
                } catch (error: any) {
                  setEstimateError((error as any)?.message || 'Failed to update estimate')
                } finally {
                  setEstimateSaving(false)
                }
              }} disabled={estimateSaving}>
                {estimateSaving ? 'Updating…' : 'Update Estimate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TabsContent value="recurring" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
              <CardTitle>Recurring Invoices</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input 
                      placeholder="Search recurring invoices..." 
                      className="pl-10 w-64" 
                      value={recurringSearch} 
                      onChange={(e) => {
                        setRecurringSearch(e.target.value)
                        setRecurringPage(1) // Reset to first page when searching
                      }} 
                    />
                  </div>
                  <select
                    className="text-sm border rounded px-3 py-2"
                    value={recurringStatus}
                    onChange={(e) => {
                      setRecurringStatus(e.target.value)
                      setRecurringPage(1) // Reset to first page when filtering
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <Button onClick={() => {
                    const safeCustomers = Array.isArray(customers) ? customers : []
                    setRecurringForm({
                      ...defaultRecurringForm,
                      customerId: safeCustomers[0]?.id || "",
                      currency: safeCustomers[0]?.currency || 'Frw',
                      startDate: new Date().toISOString().slice(0,10)
                    })
                    setEditingRecurring(null)
                    setRecurringDialogOpen(true)
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Recurring Invoice
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(recurringInvoicesQuery as any)?.isLoading && (
                <>
                  {[0,1,2,3,4].map(i => (
                    <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="h-4 w-32 bg-muted rounded" />
                      <div className="flex items-center gap-4">
                        <div className="h-4 w-20 bg-muted rounded" />
                        <div className="h-4 w-16 bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {!((recurringInvoicesQuery as any)?.isLoading) && displayRecurringInvoices.map((rec) => (
                <div
                  key={rec.id}
                  className="group flex items-center justify-between p-6 bg-white border border-gray-100 rounded-xl hover:shadow-lg hover:border-blue-200 transition-all duration-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center group-hover:from-orange-200 group-hover:to-orange-300 transition-all duration-200">
                      <span className="text-orange-600 font-bold text-lg">🔄</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">{rec.name}</p>
                      <p className="text-sm text-gray-600">{rec.customer?.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={rec.status === 'active' ? 'default' : rec.status === 'paused' ? 'secondary' : 'outline'} className="text-xs">
                          {rec.status}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {rec.frequency}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          ${(Number(rec.totalAmount) || 0).toFixed(2)} {rec.currency}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Next: {new Date(rec.nextRunDate).toLocaleDateString()}
                        {rec.lastRunDate && ` • Last: ${new Date(rec.lastRunDate).toLocaleDateString()}`}
                        {rec._count?.generatedInvoices && ` • ${rec._count.generatedInvoices} invoices`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status Management */}
                    <Select value={rec.status} onValueChange={(value) => handleStatusChange(rec, value as any)}>
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Action Buttons */}
                    <div className="flex gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleViewRecurring(rec)}
                        className="hover:bg-blue-50 hover:border-blue-200"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditRecurring(rec)}
                        className="hover:bg-orange-50 hover:border-orange-200"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      {rec.status === 'active' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleGenerateRecurring(rec)}
                          disabled={generateRecurringMutation.isPending}
                          className="hover:bg-green-50 hover:border-green-200"
                        >
                          {generateRecurringMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {!((recurringInvoicesQuery as any)?.isLoading) && displayRecurringInvoices.length === 0 && (
              <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">No recurring invoices found</p>
                  <Button onClick={() => {
                    const safeCustomers = Array.isArray(customers) ? customers : []
                    const newForm = { ...defaultRecurringForm }
                    newForm.customerId = safeCustomers[0]?.id || ""
                    newForm.currency = safeCustomers[0]?.currency || 'Frw'
                    newForm.startDate = new Date().toISOString().slice(0,10)
                    setRecurringForm(newForm)
                    setEditingRecurring(null)
                    setRecurringDialogOpen(true)
                  }}>
                  <Plus className="w-4 h-4 mr-2" />
                    Create Recurring Invoice
                </Button>
              </div>
              )}

              {recurringInvoicesQuery.data && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((recurringPage - 1) * recurringPageSize) + 1} to {Math.min(recurringPage * recurringPageSize, recurringInvoicesQuery.data.total)} of {recurringInvoicesQuery.data.total} recurring invoices
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      className="text-sm border rounded px-2 py-1"
                      value={recurringPageSize}
                      onChange={(e) => {
                        setRecurringPageSize(Number(e.target.value))
                        setRecurringPage(1)
                      }}
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRecurringPage(prev => prev - 1)}
                      disabled={!recurringInvoicesQuery.data.hasPrev || recurringInvoicesQuery.isLoading}
                    >
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {recurringPage} of {recurringInvoicesQuery.data.totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRecurringPage(prev => prev + 1)}
                      disabled={!recurringInvoicesQuery.data.hasNext || recurringInvoicesQuery.isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approvals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Invoices Pending Approval</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const pendingInvoices = (invoicesQuery.data || invoices).filter(inv => inv.status?.toLowerCase() === 'draft' || inv.status?.toLowerCase() === 'sent');
                return pendingInvoices.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      {invoicesQuery.isLoading ? 'Loading invoices...' : 'No invoices pending approval'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Total invoices: {(invoicesQuery.data || invoices).length}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-blue-600 mb-4">
                      Found {pendingInvoices.length} draft invoices for approval:
                    </p>
                    {pendingInvoices.map((invoice, index) => (
                      <div key={invoice.id} className="border rounded-lg p-4 bg-gray-50">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Invoice #{invoice.invoiceNumber || invoice.id}
                        </h4>
                        <div className="text-sm text-gray-600 mb-3">
                          <p>Status: <span className="font-medium capitalize">{invoice.status}</span></p>
                          <p>Amount: <span className="font-medium">${invoice.totalAmount || 'N/A'}</span></p>
                          <p>Customer: <span className="font-medium">
                            {((Array.isArray(customers) ? customers : []).find(c => c.id === invoice.customerId)?.name) || 'N/A'}
                          </span></p>
                          <p>Due Date: <span className="font-medium">
                            {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                          </span></p>
                        </div>
                        <div className="flex gap-2">
                          {invoice.status?.toLowerCase() === 'draft' && (
                            <Button 
                              size="sm" 
                              className="bg-teal-600 hover:bg-teal-700" 
                              disabled={markingAsSent === invoice.id}
                              onClick={async () => {
                                setMarkingAsSent(invoice.id);
                                try { 
                                  await apiService.updateInvoice(invoice.id, { status: 'active' }); 
                                  await qc.invalidateQueries({ queryKey: ["invoices"] });
                                  toast({
                                    title: 'Success',
                                    description: 'Invoice approved and sent',
                                    variant: 'default'
                                  });
                                } catch (error) {
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to approve invoice',
                                    variant: 'destructive'
                                  });
                                } finally {
                                  setMarkingAsSent(null);
                                }
                              }}
                            >
                              {markingAsSent === invoice.id ? (
                                <>
                                  <MoonLoader size="sm" className="mr-2" color="teal" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Approve
                                </>
                              )}
                            </Button>
                          )}
                          {(invoice.status?.toLowerCase() === 'sent' || invoice.status?.toLowerCase() === 'pending') && (
                            <Button 
                              size="sm" 
                              className="bg-teal-600 hover:bg-teal-700" 
                              disabled={markingAsPaid === invoice.id}
                              onClick={async () => {
                                setMarkingAsPaid(invoice.id);
                                try { 
                                  await apiService.updateInvoice(invoice.id, { status: 'paid', balanceDue: 0 }); 
                                  await qc.invalidateQueries({ queryKey: ["invoices"] });
                                  toast({
                                    title: 'Success',
                                    description: 'Invoice marked as paid',
                                    variant: 'default'
                                  });
                                } catch (error) {
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to mark invoice as paid',
                                    variant: 'destructive'
                                  });
                                } finally {
                                  setMarkingAsPaid(null);
                                }
                              }}
                            >
                              {markingAsPaid === invoice.id ? (
                                <>
                                  <MoonLoader size="sm" className="mr-2" color="teal" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Mark Paid
                                </>
                              )}
                            </Button>
                          )}
                          <Button variant="destructive" size="sm" onClick={async () => {
                            try { 
                              await apiService.updateInvoice(invoice.id, { status: 'cancelled' }); 
                              await qc.invalidateQueries({ queryKey: ["invoices"] }) 
                            } catch {}
                          }}>
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-create" className="space-y-4">
          <NaturalLanguageInvoice 
            onInvoiceCreated={(invoice) => {
              // Refresh invoices list when new invoice is created
              qc.invalidateQueries({ queryKey: ["invoices"] })
              toast({ title: 'Invoice created', description: `Invoice ${invoice.invoiceNumber} created successfully!` })
            }}
          />
        </TabsContent>

        <TabsContent value="ai-chat" className="space-y-4">
          <AIAccountingChat 
            onActionExecuted={(action, result) => {
              // Refresh relevant data when actions are executed
              qc.invalidateQueries({ queryKey: ["invoices"] })
              qc.invalidateQueries({ queryKey: ["expenses"] })
              qc.invalidateQueries({ queryKey: ["customers"] })
              qc.invalidateQueries({ queryKey: ["financial-insights"] })
              toast({ title: 'Success', description: `Action ${action} executed successfully!` })
            }}
          />
        </TabsContent>
      </Tabs>
      </div>
      {/* Send Invoice PDF Modal */}
      <Dialog open={sendEmailOpen} onOpenChange={(o) => { setSendEmailOpen(o); if (!o) { setSendEmailInvoiceId(null); setSendEmailTo('') } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Invoice PDF</DialogTitle>
            <DialogDescription>Enter the recipient email address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="send-email-to">Email</Label>
            <Input id="send-email-to" type="email" placeholder="name@example.com" value={sendEmailTo} onChange={(e) => setSendEmailTo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailOpen(false)} disabled={sendEmailLoading}>Cancel</Button>
            <Button disabled={sendEmailLoading || !sendEmailInvoiceId || !sendEmailTo} onClick={async () => {
              if (!sendEmailInvoiceId || !sendEmailTo) return
              try {
                setSendEmailLoading(true)
                
                // Find the invoice data
                const invoice = invoices.find(inv => inv.id === sendEmailInvoiceId)
                if (!invoice) {
                  toast({ title: 'Invoice Not Found', description: 'Could not find invoice data for email generation.', variant: 'destructive' })
                  return
                }

                // Find customer data
                const customer = customers.find(c => c.id === invoice.customerId)
                
                // Get company data with logo (same as journal PDF)
                let companyData = {
                  name: 'Your Company',
                  logoUrl: '',
                  primaryColor: '#009688',
                  website: '',
                  address: '',
                  city: '',
                  state: '',
                  postalCode: '',
                  email: '',
                  phone: '',
                  invoiceTerms: 'Payment is due within 30 days of invoice date.',
                  invoiceFooter: 'Thank you for your business!'
                }
                
                try {
                  const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
                  if (companyId) {
                    const company = await apiService.getCompany(companyId) as any
                    if (company) {
                      companyData.name = company.name || companyData.name
                      let logoUrl = company.logoUrl || companyData.logoUrl
                      
                      // Convert relative URL to full URL for PDF generation
                      if (logoUrl && logoUrl.startsWith('/api/')) {
                        logoUrl = getApiUrl(logoUrl)
                      }
                      
                      companyData.logoUrl = logoUrl
                      companyData.primaryColor = company.primaryColor || companyData.primaryColor
                      companyData.website = company.website || companyData.website
                      companyData.address = company.address || companyData.address
                      companyData.city = company.city || companyData.city
                      companyData.state = company.state || companyData.state
                      companyData.postalCode = company.postalCode || companyData.postalCode
                      companyData.email = company.email || companyData.email
                      companyData.phone = company.phone || companyData.phone
                    }
                  }
                } catch (error) {
                  // Use fallback data if API fails
                  companyData = {
                    name: localStorage.getItem('company_name') || 'Your Company',
                    logoUrl: localStorage.getItem('company_logoUrl') || '',
                    primaryColor: localStorage.getItem('company_primaryColor') || '#009688',
                    website: localStorage.getItem('company_website') || '',
                    address: localStorage.getItem('company_address') || '',
                    city: localStorage.getItem('company_city') || '',
                    state: localStorage.getItem('company_state') || '',
                    postalCode: localStorage.getItem('company_postalCode') || '',
                    email: localStorage.getItem('company_email') || '',
                    phone: localStorage.getItem('company_phone') || '',
                    invoiceTerms: 'Payment is due within 30 days of invoice date.',
                    invoiceFooter: 'Thank you for your business!'
                  }
                }
                
                // Generate PDF using frontend generator
                const generator = new InvoicePDFGenerator({
                  invoice: {
                    id: invoice.id,
                    invoiceNumber: invoice.invoiceNumber,
                    issueDate: invoice.issueDate,
                    dueDate: invoice.dueDate,
                    status: invoice.status,
                    totalAmount: invoice.totalAmount,
                    balanceDue: invoice.balanceDue,
                    currency: customer?.currency || 'Frw',
                    subtotal: (invoice as any).subtotal || invoice.totalAmount,
                    taxAmount: (invoice as any).taxAmount || 0,
                    discountAmount: (invoice as any).discountAmount || 0,
                    customer: customer ? {
                      name: customer.name,
                      email: customer.email,
                      address: customer.address,
                      phone: (customer as any).phone,
                      taxId: (customer as any).taxId
                    } : undefined,
                    lines: (invoice as any).lines || [],
                    notes: (invoice as any).notes,
                    paymentUrl: (invoice as any).paymentUrl
                  },
                  company: companyData
                })

                // Generate PDF blob
                const pdfBlob = await generator.generate()
                
                // Send email with frontend-generated PDF
                await apiService.sendInvoiceEmail(invoice.id, {
                  to: sendEmailTo,
                  subject: `Invoice ${invoice.invoiceNumber}`,
                  message: `Please find your invoice ${invoice.invoiceNumber} attached. Thank you for your business!`,
                  attachPdf: true,
                  pdfBlob: pdfBlob
                })
                
                toast({ title: 'Invoice sent', description: `Invoice ${invoice.invoiceNumber} sent to ${sendEmailTo}` })
                setSendEmailOpen(false)
              } catch (e: any) {
                console.error('Error sending invoice email:', e)
                
                // Fallback to backend API if frontend generation fails
                try {
                  const result = await apiService.sendInvoiceEmail(sendEmailInvoiceId, { to: sendEmailTo, attachPdf: true })
                  
                  // Check if there's a warning about SMTP not being configured
                  if (result?.warning) {
                    toast({ 
                      title: 'Email Queued', 
                      description: result.warning,
                      variant: 'default'
                    })
                  } else {
                    toast({ title: 'Invoice sent', description: `Invoice sent to ${sendEmailTo}` })
                  }
                  setSendEmailOpen(false)
                } catch (backendError: any) {
                  console.error('Backend email sending also failed:', backendError)
                  
                  // Extract error message from backend response
                  let errorMessage = 'Failed to send email';
                  if (backendError?.message) {
                    errorMessage = backendError.message;
                  } else if (backendError?.data?.message) {
                    errorMessage = backendError.data.message;
                  } else if (backendError?.data?.details) {
                    errorMessage = backendError.data.details;
                  }
                  
                  // Check for specific authentication error
                  if (errorMessage.includes('Authentication Required') || 
                      errorMessage.includes('smtp_auth_failed') ||
                      errorMessage.includes('App Password')) {
                    toast({ 
                      title: 'Email Authentication Failed', 
                      description: 'Gmail requires an App Password. Please configure SMTP settings with a Gmail App Password.',
                      variant: 'destructive'
                    })
                  } else {
                    toast({ 
                      title: 'Send failed', 
                      description: errorMessage,
                      variant: 'destructive' 
                    })
                  }
                }
              } finally {
                setSendEmailLoading(false)
              }
            }}>{sendEmailLoading ? 'Sending…' : 'Send PDF'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Modal */}
      <Dialog open={detailsOpen} onOpenChange={(open) => {
        setDetailsOpen(open)
        if (!open && detailsPdfUrl) {
          URL.revokeObjectURL(detailsPdfUrl)
          setDetailsPdfUrl(null)
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ maxWidth: 'calc(100vw - 300px)' }}>
          <DialogHeader className="pb-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-slate-900">Invoice Details</DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  {detailsInvoice ? `Invoice ${detailsInvoice.invoiceNumber}` : 'Loading invoice details...'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {detailsInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Invoice Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Invoice Number:</span>
                      <span className="font-medium">{detailsInvoice.invoiceNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Issue Date:</span>
                      <span className="font-medium">{new Date(detailsInvoice.issueDate).toLocaleDateString()}</span>
                    </div>
                    {detailsInvoice.dueDate && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Due Date:</span>
                        <span className="font-medium">{new Date(detailsInvoice.dueDate).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Status:</span>
                      <Badge 
                        variant={
                          detailsInvoice.status === "paid" ? "default" :
                          detailsInvoice.dueDate && new Date(detailsInvoice.dueDate) < new Date() && detailsInvoice.balanceDue > 0 ? "destructive" : 
                          "secondary"
                        }
                      >
                        {detailsInvoice.status.charAt(0).toUpperCase() + detailsInvoice.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Currency:</span>
                      <span className="font-medium">
                        {(() => {
                          const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                          const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                          return currency === 'USD' ? 'Frw' : currency;
                        })()}
                      </span>
                    </div>
                    
                    {/* Reconciliation Status */}
                    {detailsReconciliationStatus && (
                      <div className="pt-3 mt-3 border-t border-slate-300">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">Reconciliation Status:</span>
                          {detailsReconciliationStatus.isReconciled ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Reconciled
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Not Reconciled</Badge>
                          )}
                        </div>
                        {detailsReconciliationStatus.isReconciled && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-slate-600">
                              {detailsReconciliationStatus.reconciledPayments} of {detailsReconciliationStatus.totalPayments} payment{detailsReconciliationStatus.totalPayments !== 1 ? 's' : ''} reconciled
                            </p>
                            {detailsReconciliationStatus.bankTransactions.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <p className="text-xs font-medium text-slate-700 mb-1">Bank Transactions:</p>
                                {detailsReconciliationStatus.bankTransactions.map((bt, idx) => (
                                  <div key={idx} className="text-xs text-slate-600 flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                    {formatCurrency(bt.amount, detailsInvoice.currency || 'Frw')} - {new Date(bt.date).toLocaleDateString()}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Customer Information</h3>
                  {(() => {
                    const customer = customers.find(c => c.id === detailsInvoice.customerId)
                    return customer ? (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Name:</span>
                          <span className="font-medium">{customer.name}</span>
                        </div>
                        {customer.email && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Email:</span>
                            <span className="font-medium">{customer.email}</span>
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Phone:</span>
                            <span className="font-medium">{customer.phone}</span>
                          </div>
                        )}
                        {customer.address && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Address:</span>
                            <span className="font-medium">{customer.address}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">Customer information not available</p>
                    )
                  })()}
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Financial Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total Amount:</span>
                      <span className="font-semibold text-lg">
                        {(() => {
                          const customer = allCustomers.find(c => c.id === detailsInvoice.customerId)
                          const currency = customer?.currency || detailsInvoice.currency || 'Frw'
                          const displayCurrency = currency === 'USD' ? 'Frw' : currency
                          const converted = convertedInvoiceAmounts[detailsInvoice.id]
                          const totalAmountToDisplay = currency === 'USD' && converted ? converted.totalAmount : detailsInvoice.totalAmount
                          return formatCurrency(totalAmountToDisplay, displayCurrency)
                        })()}
                      </span>
                    </div>
                    {detailsInvoice.balanceDue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Balance Due:</span>
                        <span className="font-semibold text-amber-600">
                          {(() => {
                            const customer = allCustomers.find(c => c.id === detailsInvoice.customerId)
                            const currency = customer?.currency || detailsInvoice.currency || 'Frw'
                            const displayCurrency = currency === 'USD' ? 'Frw' : currency
                            const converted = convertedInvoiceAmounts[detailsInvoice.id]
                            const balanceDueToDisplay = currency === 'USD' && converted ? converted.balanceDue : detailsInvoice.balanceDue
                            return formatCurrency(balanceDueToDisplay, displayCurrency)
                          })()}
                        </span>
                      </div>
                    )}
                    {detailsInvoice.balanceDue === 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Payment Status:</span>
                        <span className="font-semibold text-green-600">Fully Paid</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {detailsInvoice.dueDate && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Days Until Due:</span>
                        <span className={`font-medium ${
                          new Date(detailsInvoice.dueDate) < new Date() ? 'text-red-600' : 'text-slate-900'
                        }`}>
                          {Math.ceil((new Date(detailsInvoice.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Line Items */}
              {detailsInvoice.lines && detailsInvoice.lines.length > 0 ? (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Products & Services</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Unit Price</TableHead>
                          <TableHead>Discount</TableHead>
                          <TableHead>Tax Rate</TableHead>
                          <TableHead className="text-right">Line Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailsInvoice.lines.map((line: any, index: number) => (
                          <TableRow key={line.id || index}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                                  <Package className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="font-medium text-slate-900">{line.description}</div>
                                  {line.product && (
                                    <div className="text-sm text-slate-500">
                                      SKU: {line.product.sku || 'N/A'} | 
                                      Category: {line.product.categoryObject?.name || 'Uncategorized'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{Number(line.quantity) || 0}</div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {(() => {
                                  const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                                  const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                                  const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                                  return formatCurrency(Number(line.unitPrice) || 0, displayCurrency);
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {(() => {
                                  const discountRate = Number(line.discountRate) || 0;
                                  const discountAmount = Number(line.discountAmount) || 0;
                                  const lineTotal = Number(line.quantity) * Number(line.unitPrice);
                                  const lineDiscountAmount = discountAmount > 0 ? discountAmount : (lineTotal * (discountRate / 100));
                                  // Check if invoice has global discount or line has discount
                                  const invoiceDiscountTotal = Number((detailsInvoice as any).discountAmount) || 0;
                                  const hasInvoiceDiscount = invoiceDiscountTotal > 0;
                                  // Get currency with USD to Frw conversion
                                  const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                                  const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                                  const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                                  // Check if there's actual discount (either from discountRate, calculated, or invoice-level discount)
                                  if (discountRate > 0 || lineDiscountAmount > 0 || hasInvoiceDiscount) {
                                    return (
                                      <span className="text-green-600 font-medium">
                                        {discountRate > 0 ? `${discountRate}%` : (hasInvoiceDiscount ? 'Discount Applied' : formatCurrency(lineDiscountAmount, displayCurrency))}
                                      </span>
                                    );
                                  }
                                  return <span className="text-slate-400">No discount</span>;
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {(() => {
                                  const taxRate = Number(line.taxRate) || 0
                                  const lineTotal = Number(line.quantity) * Number(line.unitPrice)
                                  const lineTaxAmount = lineTotal * (taxRate / 100)
                                  // Check if invoice has global tax or line has tax
                                  const invoiceTaxTotal = Number((detailsInvoice as any).taxTotal) || Number((detailsInvoice as any).taxAmount) || 0
                                  const hasInvoiceTax = invoiceTaxTotal > 0
                                  // Check if there's actual tax (either from taxRate, calculated, or invoice-level tax)
                                  if (taxRate > 0 || lineTaxAmount > 0 || hasInvoiceTax) {
                                    return (
                                      <span className="text-blue-600 font-medium">
                                        {taxRate > 0 ? `${taxRate}%` : (hasInvoiceTax ? 'Tax Applied' : 'Tax Applied')}
                                      </span>
                                    )
                                  }
                                  return <span className="text-slate-400">No tax</span>
                                })()}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="font-semibold text-slate-900">
                                {(() => {
                                  const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                                  const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                                  const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                                  return formatCurrency(line.lineTotal || (Number(line.quantity) * Number(line.unitPrice)), displayCurrency);
                                })()}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Line Items Summary */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Subtotal:</span>
                          <span className="font-medium">
                            {(() => {
                              const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                              const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                              return formatCurrency(detailsInvoice.lines.reduce((sum: number, line: any) => 
                                sum + (Number(line.quantity) * Number(line.unitPrice)), 0
                              ), displayCurrency);
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Discount:</span>
                          <span className="font-medium text-green-600">
                            {(() => {
                              // Get currency with USD to Frw conversion
                              const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                              const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                              
                              // First, check invoice-level discount
                              const invoiceDiscountAmount = Number((detailsInvoice as any).discountAmount) || 0;
                              
                              if (invoiceDiscountAmount > 0) {
                                return `-${formatCurrency(invoiceDiscountAmount, displayCurrency)}`;
                              }
                              
                              // Calculate discount from line items
                              const lineDiscounts = detailsInvoice.lines.reduce((sum: number, line: any) => {
                                const discountRate = Number(line.discountRate) || 0;
                                const discountAmount = Number(line.discountAmount) || 0;
                                
                                if (discountAmount > 0) {
                                  return sum + discountAmount;
                                } else if (discountRate > 0) {
                                  const lineTotal = Number(line.quantity) * Number(line.unitPrice);
                                  return sum + (lineTotal * (discountRate / 100));
                                }
                                return sum;
                              }, 0);
                              
                              if (lineDiscounts > 0) {
                                return `-${formatCurrency(lineDiscounts, displayCurrency)}`;
                              }
                              
                              return formatCurrency(0, displayCurrency);
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Tax:</span>
                          <span className="font-medium text-blue-600">
                            {(() => {
                              // Get currency with USD to Frw conversion
                              const customer = allCustomers.find(c => c.id === detailsInvoice.customerId);
                              const currency = customer?.currency || detailsInvoice.currency || 'Frw';
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                              
                              // First, try to use invoice-level tax
                              const invoiceTaxTotal = Number((detailsInvoice as any).taxTotal) || Number((detailsInvoice as any).taxAmount) || 0
                              
                              if (invoiceTaxTotal > 0) {
                                return formatCurrency(invoiceTaxTotal, displayCurrency)
                              }
                              
                              // Calculate tax from line items - try multiple methods
                              const calculatedTax = detailsInvoice.lines.reduce((sum: number, line: any) => {
                                // Method 1: Use taxRate if available
                                const taxRate = Number(line.taxRate) || 0;
                                if (taxRate > 0) {
                                  const lineSubtotal = Number(line.quantity) * Number(line.unitPrice);
                                  return sum + (lineSubtotal * (taxRate / 100));
                                }
                                
                                // Method 2: Calculate from lineTotal difference (if lineTotal includes tax)
                                const lineSubtotal = Number(line.quantity) * Number(line.unitPrice);
                                const lineTotal = Number(line.lineTotal) || lineSubtotal;
                                const lineTax = lineTotal - lineSubtotal;
                                
                                // Method 3: Use line.taxAmount if available
                                const lineTaxAmount = Number(line.taxAmount) || 0;
                                
                                // Use the first available method
                                return sum + (lineTaxAmount > 0 ? lineTaxAmount : (lineTax > 0 ? lineTax : 0));
                              }, 0)
                              
                              return formatCurrency(calculatedTax, displayCurrency)
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Items Count:</span>
                          <span className="font-medium">
                            {detailsInvoice.lines.length} items
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Quantity:</span>
                          <span className="font-medium">
                            {detailsInvoice.lines.reduce((sum: number, line: any) => sum + Number(line.quantity), 0)} units
                          </span>
                        </div>
                        <div className="flex justify-between font-semibold text-lg pt-2 border-t border-slate-200">
                          <span className="text-slate-900">Grand Total:</span>
                          <span className="text-slate-900">
                            {(() => {
                              const customer = allCustomers.find(c => c.id === detailsInvoice.customerId)
                              const currency = customer?.currency || detailsInvoice.currency || 'Frw'
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency
                              const converted = convertedInvoiceAmounts[detailsInvoice.id]
                              const totalAmountToDisplay = currency === 'USD' && converted ? converted.totalAmount : detailsInvoice.totalAmount
                              return formatCurrency(totalAmountToDisplay, displayCurrency)
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900">Product Details</h3>
                      <p className="text-blue-700 text-sm">
                        This invoice was created via URUTIQ-accounting systems. Product line items are stored in the invoice but may not be fully loaded in this view.
                      </p>
                      <div className="mt-2 text-xs text-blue-600">
                        Debug: Invoice has {detailsInvoice.lines?.length || 0} line items
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadInvoicePdf(detailsInvoice.id, detailsInvoice.invoiceNumber)}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => {
                    const customer = customers.find(c => c.id === detailsInvoice.customerId)
                    setSendEmailInvoiceId(detailsInvoice.id)
                    setSendEmailTo(customer?.email || '')
                    setSendEmailOpen(true)
                    setDetailsOpen(false)
                  }}
                  className="flex-1"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Credit Note Dialog */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Credit Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">Reason</div>
            <Textarea value={creditReason} onChange={(e) => setCreditReason(e.target.value)} rows={3} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
              <Button onClick={async () => {
                if (!creditInvoiceId) return;
                try {
                  const API = getApiUrl()
                  const res = await fetch(`${API}/api/credit-notes/${selectedCompany}`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
                      'x-tenant-id': localStorage.getItem('tenant_id') || 'tenant_demo',
                    },
                    body: JSON.stringify({ invoiceId: creditInvoiceId, reason: creditReason, lines: [{ description: 'Credit', quantity: 1, unitPrice: 0 }] })
                  })
                  await res.json()
                  setCreditDialogOpen(false)
                } catch {}
              }}>Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-600">Delete Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete invoice <strong>{invoiceToDelete?.invoiceNumber}</strong>? 
              The invoice will be hidden from the main list but can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteConfirmOpen(false);
                setInvoiceToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              disabled={invoiceToDelete ? deletingInvoice === invoiceToDelete.id : false}
              onClick={() => {
                if (invoiceToDelete) {
                  handleDeleteInvoice(invoiceToDelete.id);
                }
              }}
            >
              {invoiceToDelete && deletingInvoice === invoiceToDelete.id ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="gray" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Invoice Dialog */}
      <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
        <DialogContent className="!max-w-7xl max-h-[95vh] overflow-y-auto w-[95vw] md:max-h-[90vh] md:w-[90vw]">
          <DialogHeader>
            <DialogTitle>{editingRecurring ? 'Edit Recurring Invoice' : 'Create Recurring Invoice'}</DialogTitle>
            <DialogDescription>
              Set up a recurring invoice template that will automatically generate invoices on schedule.
            </DialogDescription>
          </DialogHeader>
          <EnhancedRecurringInvoiceForm
            formData={recurringForm}
            onChange={setRecurringForm}
            customers={customers}
            isEditing={!!editingRecurring}
          />

          {recurringError && (
            <div className="text-red-600 text-sm">{recurringError}</div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecurringDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              disabled={recurringSaving || !isRecurringFormValid(recurringForm)}
              onClick={async () => {
                try {
                  setRecurringSaving(true)
                  setRecurringError(null)
                  
                  if (editingRecurring) {
                    await updateRecurringInvoiceMutation.mutateAsync({
                      id: editingRecurring.id,
                      data: recurringForm
                    })
                  } else {
                    await createRecurringInvoiceMutation.mutateAsync(recurringForm)
                  }
                } catch (error: any) {
                  setRecurringError((error as any)?.message || 'Failed to save recurring invoice')
                } finally {
                  setRecurringSaving(false)
                }
              }}
            >
              {recurringSaving ? 'Saving...' : (editingRecurring ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
        </Dialog>

        {/* Recurring Invoice Details Dialog */}
        <Dialog open={recurringDetailsOpen} onOpenChange={setRecurringDetailsOpen}>
          <DialogContent className="!max-w-7xl max-h-[95vh] overflow-y-auto w-[95vw] md:max-h-[90vh] md:w-[90vw]">
            <DialogHeader>
              <DialogTitle>Recurring Invoice Details</DialogTitle>
              <DialogDescription>
                View details and generated invoices for this recurring template.
              </DialogDescription>
            </DialogHeader>
            
            {selectedRecurring && (
              <div className="space-y-6">
                {/* Reuse the same form in read-only mode for a consistent view */}
                {(() => {
                  const lines = (selectedRecurring.lines || []).map(l => ({
                    description: l.description || '',
                    quantity: Number(l.quantity) || 0,
                    unitPrice: Number(l.unitPrice) || 0,
                    taxRate: Number(l.taxRate) || 0,
                  }))
                  const totals = recalcRecurringTotals(lines)
                  const viewData = {
                    customerId: selectedRecurring.customerId,
                    name: selectedRecurring.name,
                    description: selectedRecurring.description || '',
                    frequency: selectedRecurring.frequency as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom',
                    interval: selectedRecurring.interval || 1,
                    startDate: (selectedRecurring.startDate || '').slice(0,10),
                    endDate: selectedRecurring.endDate ? selectedRecurring.endDate.slice(0,10) : '',
                    currency: selectedRecurring.currency || 'Frw',
                    notes: selectedRecurring.notes || '',
                    terms: selectedRecurring.terms || '',
                    dueDateOffset: selectedRecurring.dueDateOffset || 0,
                    autoSend: !!selectedRecurring.autoSend,
                    emailTemplate: selectedRecurring.emailTemplate || '',
                    // Advanced Scheduling defaults (not persisted in API yet)
                    dayOfWeek: undefined,
                    dayOfMonth: undefined,
                    businessDaysOnly: false,
                    skipHolidays: false,
                    timezone: 'UTC',
                    // Conditional Logic defaults
                    skipIfOutstandingBalance: false,
                    maxOutstandingAmount: undefined,
                    skipIfCustomerInactive: false,
                    requireApproval: false,
                    approvalWorkflowId: undefined,
                    // Email settings defaults
                    ccEmails: [],
                    bccEmails: [],
                    reminderDays: [],
                    lines,
                    subtotal: totals.subtotal,
                    taxTotal: totals.taxTotal,
                    totalAmount: totals.totalAmount,
                  }
                  return (
                    <EnhancedRecurringInvoiceForm
                      formData={viewData}
                      onChange={() => {}}
                      customers={customers}
                      isEditing={false}
                      readOnly
                    />
                  )
                })()}

                {/* Generated Invoices History */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Generated Invoices</h4>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => loadRecurringHistory(selectedRecurring.id)}
                      disabled={recurringHistoryLoading}
                    >
                      {recurringHistoryLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Refresh
                    </Button>
                  </div>
                  
                  {recurringHistoryLoading ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">Loading invoice history...</p>
                    </div>
                  ) : recurringHistory.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {recurringHistory.map((invoice) => (
                        <div key={invoice.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                          <div>
                            <p className="font-medium">{invoice.invoiceNumber}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(invoice.createdAt).toLocaleDateString()} • 
                              Status: <Badge variant="outline" className="ml-1">{invoice.status}</Badge>
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(invoice.totalAmount, invoice.currency)}</p>
                            {invoice.payments?.length > 0 && (
                              <p className="text-sm text-green-600">
                                {invoice.payments.length} payment(s)
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No invoices generated yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  )
}
